import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Background, BackgroundVariant, ReactFlow, ReactFlowProvider,
  SelectionMode, applyEdgeChanges, applyNodeChanges, useReactFlow,
} from '@xyflow/react';
import {
  AlignHorizontalDistributeCenter, Check, ChevronDown, CircleUserRound, Coins, FileAudio, HardDrive,
  FileImage, FileVideo, ImagePlus, Images, LoaderCircle, LogOut, Map as MapIcon, Maximize2, Minus,
  Moon, Plus, Redo2, Save, Settings, Sun, Undo2,
  Upload, Video, X, ZoomIn, ListChecks,
} from 'lucide-react';
import { api, getToken } from '../lib/api.js';
import CanvasAssetLibrary from './CanvasAssetLibrary.jsx';
import CanvasMaterialSaveDialog from './CanvasMaterialSaveDialog.jsx';
import CanvasMediaPreview from './CanvasMediaPreview.jsx';
import CanvasSettingsPanel from './CanvasSettingsPanel.jsx';
import CanvasTaskMonitor from './CanvasTaskMonitor.jsx';
import ReferenceEdge from './ReferenceEdge.jsx';
import { ImageGeneratorNode, MediaNode, VideoGeneratorNode } from './nodes.jsx';
import {
  arrangeCanvas, assetRatio, canvasImagePreviewUrl, createGeneratorNode, createMediaNode, duplicatePosition, fileKind, findVisiblePosition, nodeSize,
  readMediaMetadata, serializableCanvas, uid, generatorPreviewSize,
} from './canvas-utils.js';

const edgeTypes = { reference: ReferenceEdge };
const nodeTypes = { media: MediaNode, imageGenerator: ImageGeneratorNode, videoGenerator: VideoGeneratorNode };
const ACTIVE_STATES = new Set(['queued', 'processing', 'running', 'pending']);

function droppedUrl(dataTransfer) {
  const uriList = String(dataTransfer?.getData('text/uri-list') || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .find((item) => item && !item.startsWith('#'));
  const plain = String(dataTransfer?.getData('text/plain') || '').trim();
  const candidate = uriList || plain;
  return /^https?:\/\//i.test(candidate) ? candidate : '';
}

function droppedMime(url) {
  const clean = String(url || '').split(/[?#]/, 1)[0].toLowerCase();
  if (/\.(?:png|jpe?g|webp|gif|bmp|avif)$/i.test(clean)) return 'image/jpeg';
  if (/\.(?:mp4|webm|mov|m4v)$/i.test(clean)) return 'video/mp4';
  if (/\.(?:mp3|wav|m4a|aac|ogg)$/i.test(clean)) return 'audio/mpeg';
  return '';
}

async function fileFromDroppedUrl(url) {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw new Error(`外部素材读取失败：HTTP ${response.status}`);
  const blob = await response.blob();
  const type = blob.type || droppedMime(url);
  if (!type.startsWith('image/') && !type.startsWith('video/') && !type.startsWith('audio/')) {
    throw new Error('外部拖入内容不是支持的图片、视频或音频文件');
  }
  const name = decodeURIComponent(String(url).split(/[?#]/, 1)[0].split('/').pop() || '外部素材');
  return new File([blob], name.includes('.') ? name : `外部素材.${type.split('/')[1] || 'bin'}`, { type });
}

function CanvasConnectionLine({ fromX, fromY, toX, toY }) {
  const span = Math.abs(toX - fromX);
  const control = Math.max(72, Math.min(240, span * 0.48));
  const direction = toX >= fromX ? 1 : -1;
  const path = `M ${fromX},${fromY} C ${fromX + control * direction},${fromY} ${toX - control * direction},${toY} ${toX},${toY}`;
  return <g className="canvas-connection-preview">
    <path className="canvas-connection-line canvas-connection-line--tube" d={path} />
    <path className="canvas-connection-line canvas-connection-line--energy" d={path} />
    <path className="canvas-connection-line canvas-connection-line--spark" d={path} />
  </g>;
}

function miniMapColor(node) {
  if (node.type === 'videoGenerator' || node.data?.kind === 'video') return '#ff4f62';
  if (node.data?.kind === 'audio') return '#f2c34e';
  return '#3d8cff';
}

function miniMapNodeSize(node) {
  // Generator nodes grow when selected because their prompt and parameters
  // unfold below the preview. The minimap represents the stable canvas
  // footprint, so use the collapsed preview dimensions here.
  if (node.type === 'imageGenerator' || node.type === 'videoGenerator') {
    const preview = generatorPreviewSize(node.data?.ratio || (node.type === 'videoGenerator' ? '16:9' : '1:1'));
    return { width: Math.max(430, preview.width), height: preview.height + 38 };
  }
  return nodeSize(node);
}

function CanvasMiniMap({ nodes, viewport, viewportVersion, wrapperRef, theme, setCenter, pulseNodeId }) {
  const draggingRef = useRef(false);
  const geometry = useMemo(() => {
    if (!nodes.length) return { nodes: [], x: 0, y: 0, width: 100, height: 64 };
    const mapped = nodes.map((node) => {
      const fallback = miniMapNodeSize(node);
      return {
        id: node.id,
        x: Number(node.position?.x || 0),
        y: Number(node.position?.y || 0),
        // ReactFlow's measured dimensions include transient expanded panels and
        // zoom rounding. The minimap must use the same stable footprint as the
        // canvas node so its rectangles never stretch when a node is opened.
        width: Math.max(8, Number(fallback.width)),
        height: Math.max(8, Number(fallback.height)),
        color: miniMapColor(node),
        selected: Boolean(node.selected),
      };
    });
    const minX = Math.min(...mapped.map((node) => node.x));
    const minY = Math.min(...mapped.map((node) => node.y));
    const maxX = Math.max(...mapped.map((node) => node.x + node.width));
    const maxY = Math.max(...mapped.map((node) => node.y + node.height));
    const width = Math.max(100, maxX - minX);
    const height = Math.max(64, maxY - minY);
    // Keep the preview fitted to the actual node bounds. Large padding was the
    // source of the black empty band and made the viewport rectangle drift.
    const padding = Math.max(14, Math.min(28, Math.max(width, height) * 0.018));
    return { nodes: mapped, x: minX - padding, y: minY - padding, width: width + padding * 2, height: height + padding * 2 };
  }, [nodes]);

  const visibleViewport = useMemo(() => {
    void viewportVersion;
    const zoom = Math.max(0.01, Number(viewport?.zoom || 1));
    const wrapper = wrapperRef.current;
    if (!wrapper) return null;
    const left = -Number(viewport?.x || 0) / zoom;
    const top = -Number(viewport?.y || 0) / zoom;
    const right = left + wrapper.clientWidth / zoom;
    const bottom = top + wrapper.clientHeight / zoom;
    const x = Math.max(geometry.x, left);
    const y = Math.max(geometry.y, top);
    const clippedRight = Math.min(geometry.x + geometry.width, right);
    const clippedBottom = Math.min(geometry.y + geometry.height, bottom);
    if (clippedRight <= x || clippedBottom <= y) return null;
    return { x, y, width: clippedRight - x, height: clippedBottom - y };
  }, [geometry, viewport, viewportVersion, wrapperRef]);

  const navigate = useCallback((event, duration = 0) => {
    const svg = event.currentTarget;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const matrix = svg.getScreenCTM()?.inverse();
    if (!matrix) return;
    const target = point.matrixTransform(matrix);
    setCenter(target.x, target.y, { zoom: Math.max(0.08, Number(viewport?.zoom || 1)), duration });
  }, [setCenter, viewport?.zoom]);

  const map = <div className="canvas-minimap-shell nodrag nopan" data-theme={theme}>
    <svg
      className="canvas-minimap-svg"
      role="img"
      aria-label="画布小地图"
      viewBox={`${geometry.x} ${geometry.y} ${geometry.width} ${geometry.height}`}
      preserveAspectRatio="xMidYMid meet"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        const nodeId = event.target?.dataset?.nodeId;
        if (nodeId) {
          const targetNode = geometry.nodes.find((item) => item.id === nodeId);
          if (targetNode) {
            event.stopPropagation();
            setCenter(targetNode.x + targetNode.width / 2, targetNode.y + targetNode.height / 2, { zoom: Math.max(0.08, Number(viewport?.zoom || 1)), duration: 360 });
            return;
          }
        }
        draggingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        navigate(event);
      }}
      onPointerMove={(event) => { if (draggingRef.current) navigate(event); }}
      onPointerUp={(event) => {
        draggingRef.current = false;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={() => { draggingRef.current = false; }}
    >
      <rect className="canvas-minimap-svg__background" x={geometry.x} y={geometry.y} width={geometry.width} height={geometry.height} />
      {geometry.nodes.map((node) => <rect
        key={node.id}
        className={`canvas-minimap-svg__node ${node.id === pulseNodeId ? 'is-pulse' : ''}`}
        x={node.x}
        y={node.y}
        width={node.width}
        height={node.height}
        rx={Math.max(5, Math.min(18, Math.min(node.width, node.height) * 0.06))}
        fill={node.color}
        stroke={node.selected ? '#ffffff' : node.color}
        data-selected={node.selected ? 'true' : 'false'}
        data-node-id={node.id}
      />)}
      {visibleViewport && <rect className="canvas-minimap-svg__viewport" {...visibleViewport} />}
    </svg>
    <div className="canvas-minimap-label">点击框定位到对应节点</div>
  </div>;
  return createPortal(map, document.body);
}

function taskResults(task) {
  return (task?.imageUrls || task?.images || task?.result?.image_urls || [])
    .map((item, index) => {
      const url = typeof item === 'string' ? item : item?.url;
      return { index, url, previewUrl: canvasImagePreviewUrl(url, item?.previewUrl) };
    })
    .filter((item) => item.url);
}

function materializeImageOutputs(nodes, generator, images) {
  if (!generator || generator.type !== 'imageGenerator' || !generator.data?.taskId || !images?.length) return nodes;
  const requestedCount = Math.max(1, Number(generator.data?.count) || (images.length > 1 ? images.length : 1));
  if (requestedCount === 1) return nodes;
  let next = nodes;
  const generatorSize = nodeSize(generator);
  images.forEach((result, index) => {
    const sourceIndex = Number.isInteger(result?.index) ? result.index : index;
    if (!result?.url || next.some((node) => node.type === 'media'
      && String(node.data?.sourceTaskId || '') === String(generator.data.taskId)
      && Number(node.data?.sourceIndex) === sourceIndex)) return;
    const taskId = String(generator.data.taskId);
    const draft = createMediaNode({
      kind: 'image',
      name: `${generator.data.title || '生成图片'} ${sourceIndex + 1}`,
      url: result.url,
      previewUrl: result.previewUrl || canvasImagePreviewUrl(result.url),
      downloadUrl: result.downloadUrl || result.url,
      ratio: generator.data.ratio || '1:1',
    }, { x: 0, y: 0 });
    draft.data = {
      ...draft.data,
      sourceTaskId: taskId,
      sourceIndex,
      generatedFromNodeId: generator.id,
      taskId,
      imageIndex: sourceIndex,
    };
    const size = nodeSize(draft);
    const desiredCenter = {
      x: generator.position.x + generatorSize.width + size.width / 2 + 96,
      y: generator.position.y + size.height / 2 + sourceIndex * (size.height + 72),
    };
    draft.position = findVisiblePosition(next, desiredCenter, size, { gap: 72 }).position;
    next = [...next, draft];
  });
  return next;
}

function materializePersistedImageOutputs(nodes) {
  return nodes.reduce((current, node) => {
    if (node.type !== 'imageGenerator' || ACTIVE_STATES.has(node.data?.status) || !(node.data?.results || []).length) return current;
    return materializeImageOutputs(current, current.find((item) => item.id === node.id) || node, node.data?.results || []);
  }, nodes);
}

function sourceOption(node, outputIndex) {
  if (!node) return null;
  if (node.type === 'media' && ['image', 'video', 'audio'].includes(node.data?.kind) && node.data?.url) {
    const kind = node.data.kind;
    return { id: node.id, kind, name: node.data.name || `${kind === 'image' ? '图片' : kind === 'video' ? '视频' : '音频'}素材`, url: node.data.url, previewUrl: kind === 'image' ? canvasImagePreviewUrl(node.data.url, node.data.previewUrl) : node.data.previewUrl || '', outputIndex: 0 };
  }
  if (node.type === 'imageGenerator') {
    const results = node.data?.results || [];
    const index = Math.min(Math.max(0, Number(outputIndex ?? node.data?.activeResult ?? 0)), Math.max(0, results.length - 1));
    const result = results[index];
    if (result?.url) return { id: node.id, name: `${node.data.title || '生成图片'} ${index + 1}`, url: result.url, previewUrl: canvasImagePreviewUrl(result.url, result.previewUrl), outputIndex: index };
  }
  return null;
}

function sourceConnectionOption(node, targetType = '') {
  const imageOption = sourceOption(node);
  if (!imageOption) return null;
  if (!targetType || targetType === 'videoGenerator') return imageOption;
  return imageOption.kind === 'image' ? imageOption : null;
}

function isConnectionSource(node, targetType = '') {
  return Boolean(node && sourceConnectionOption(node, targetType));
}

function readableAssetName(value) {
  const name = String(value || '').trim();
  if (/^(?:material|canvas|image|video|audio)[-_][a-z0-9_-]{12,}$/i.test(name)) return '';
  return name;
}

function CanvasTopbar({ project, projects, saveState, canUndo, canRedo, user, theme, onTheme, onProject, onCreateProject, onRenameProject, onDeleteProject, onUndo, onRedo, onFit, accountActions }) {
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [editingName, setEditingName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const embeddedHost = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === 'zaomeng-canvas';
  const homeHref = '/index.html';
  const createHref = '/create.html';
  const navigateHost = useCallback((event, href) => {
    event.preventDefault();
    if (embeddedHost && window.parent && window.parent !== window) {
      // The wrapper and the React app are same-origin; keep navigation in the
      // outer page so the shared header and history remain coherent.
      window.parent.postMessage({ type: 'zaomeng:navigate', href }, '*');
      return;
    }
    window.location.assign(href);
  }, [embeddedHost]);
  useEffect(() => {
    if (!projectsOpen) return undefined;
    const closeOnOutside = (event) => {
      if (!event.target.closest?.('.canvas-project-switcher')) setProjectsOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutside, true);
    return () => document.removeEventListener('pointerdown', closeOnOutside, true);
  }, [projectsOpen]);
  const commitName = async () => {
    const name = editingName.trim().slice(0, 20);
    if (!editingId || !name) return setEditingId('');
    await onRenameProject?.(editingId, name);
    setEditingId('');
  };
  const commitNewProject = async (event) => {
    event?.preventDefault();
    const name = editingName.trim().slice(0, 20);
    if (!name) return;
    await onCreateProject?.(name);
    setEditingName('');
    setCreatingProject(false);
    setProjectsOpen(false);
  };
  return <header className="canvas-topbar">
    <div className="canvas-project-switcher">
      <button type="button" className="project-trigger" onClick={() => setProjectsOpen((value) => !value)}>
        <span className="canvas-product-mark">Z</span><strong>{project?.name || '未命名项目'}</strong><ChevronDown size={15} />
      </button>
      {projectsOpen && <div className="canvas-popover project-menu">
        <div className="popover-label">项目画布</div>
        {projects.map((item) => <div className={`project-menu-item ${item.id === project?.id ? 'is-active' : ''}`} key={item.id}>
          {editingId === item.id ? <span className="project-name-editor"><input autoFocus maxLength={20} value={editingName} onChange={(event) => setEditingName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void commitName(); if (event.key === 'Escape') setEditingId(''); }} /><button type="button" className="is-confirm" title="确认名称" onClick={() => void commitName()}><Check size={13} /></button><button type="button" title="取消修改" onClick={() => setEditingId('')}><X size={13} /></button></span> : <button type="button" onDoubleClick={(event) => { if (item.isDefault) return; event.preventDefault(); event.stopPropagation(); setEditingId(item.id); setEditingName(item.name); }} onClick={() => { onProject(item.id); setProjectsOpen(false); }}><span>{item.name}</span>{item.id === project?.id && <i />}</button>}
          {editingId !== item.id && <span className="project-menu-actions"><button type="button" title={item.isDefault ? '默认项目不能重命名' : '编辑项目名称'} disabled={item.isDefault} onClick={() => { setEditingId(item.id); setEditingName(item.name); }}><Settings size={13} /></button><button type="button" title={item.id === project?.id ? '当前项目不能删除' : item.isDefault ? '默认项目不能删除' : '删除项目'} disabled={item.id === project?.id || item.isDefault} onClick={() => setConfirmDeleteId(item.id)}><X size={13} /></button></span>}
          {confirmDeleteId === item.id && <span className="project-delete-confirm"><b>删除?</b><button type="button" onClick={() => { void onDeleteProject?.(item.id); setConfirmDeleteId(''); setProjectsOpen(false); }}>删除</button><button type="button" onClick={() => setConfirmDeleteId('')}>取消</button></span>}
        </div>)}
        {creatingProject ? <form className="project-create-inline" onSubmit={commitNewProject}>
          <input autoFocus maxLength={20} value={editingName} onChange={(event) => setEditingName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Escape') { setCreatingProject(false); setEditingName(''); } }} placeholder="输入项目名称" aria-label="新项目名称" />
          <button type="submit" className="is-confirm" disabled={!editingName.trim()} title="创建项目"><Check size={15} /></button>
          <button type="button" title="取消创建" onClick={() => { setCreatingProject(false); setEditingName(''); }}><X size={15} /></button>
        </form> : <button type="button" className="project-create" onClick={() => { setEditingName(''); setCreatingProject(true); }}><Plus size={15} />新建项目</button>}
      </div>}
    </div>
    <div className={`canvas-save-state is-${saveState}`}>
      {saveState === 'saving' ? <LoaderCircle className="spin" size={13} /> : <Save size={13} />}
      {saveState === 'saving' ? '保存中' : saveState === 'error' ? '保存失败' : '已保存'}
    </div>
    <nav className="canvas-island-nav" aria-label="造梦导航">
      <a href={homeHref} title="返回首页" onClick={(event) => navigateHost(event, homeHref)}>首页</a>
      <a href={createHref} title="打开 AI 创作" onClick={(event) => navigateHost(event, createHref)}>AI创作</a>
      <a href="#" className="is-active" aria-current="page" title="当前画布" onClick={(event) => event.preventDefault()}>无限画布</a>
      <a href="#" className="is-disabled" aria-disabled="true" onClick={(event) => event.preventDefault()}>AI工具</a>
    </nav>
    <div className="canvas-topbar__center">
      <button type="button" disabled={!canUndo} onClick={onUndo} title="撤销"><Undo2 size={17} /></button>
      <button type="button" disabled={!canRedo} onClick={onRedo} title="重做"><Redo2 size={17} /></button>
      <button type="button" onClick={onFit} title="适应工作区"><Maximize2 size={17} /></button>
    </div>
    <div className="canvas-topbar__actions">
      <button type="button" className="points-button" onClick={accountActions.consumption}><Coins size={15} /><b>{Number(user?.points || 0).toFixed(2).replace(/\.00$/, '')}</b></button>
      <button type="button" onClick={onTheme} title="切换黑白画布">{theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}</button>
      <div className="account-switcher">
        <button type="button" onClick={() => setAccountOpen((value) => !value)} title="账户"><CircleUserRound size={19} /></button>
        {accountOpen && <div className="canvas-popover account-menu">
          <div className="account-summary"><strong>{user?.username}</strong><small>{user?.role === 'admin' ? '管理员' : '创作者'}</small></div>
          <div className="account-points-row"><Coins size={15} /><strong>{Number(user?.points || 0).toFixed(2).replace(/\.00$/, '')}</strong><span>积分余额</span></div>
          <div className="account-storage-row"><HardDrive size={15} /><strong>{Number(user?.storage?.usedBytes || 0) / (1024 ** 3) < 0.005 ? '0.00' : (Number(user?.storage?.usedBytes || 0) / (1024 ** 3)).toFixed(2)}GB</strong><span>/ {(Number(user?.storage?.quotaBytes || 0) / (1024 ** 3)).toFixed(2)}GB</span></div>
          <button type="button" onClick={() => { accountActions.recharge?.(); setAccountOpen(false); }}><Coins size={15} />充值积分</button>
          <button type="button" onClick={() => { accountActions.consumption(); setAccountOpen(false); }}><Coins size={15} />消费明细</button>
          <button type="button" onClick={accountActions.logout}><LogOut size={15} />退出登录</button>
        </div>}
      </div>
    </div>
  </header>;
}

function CanvasWorkspaceInner({ project, projects, materials = [], tasks = [], videoTasks = [], config, user, theme, onTheme, onProject, onCreateProject, onRenameProject, onDeleteProject, onRetryTask, onSessionRefresh, onNotify, accountActions }) {
  const wrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingPickerRef = useRef({ kind: 'image', position: null, nodeId: '' });
  const saveTimerRef = useRef(null);
  const saveSequenceRef = useRef(0);
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  const viewportRef = useRef({ x: 0, y: 0, zoom: 1 });
  const historyRef = useRef({ undo: [], redo: [] });
  const dragSnapshotRef = useRef(null);
  const dragSnapRef = useRef(null);
  const guideFrameRef = useRef(0);
  const guidePendingRef = useRef({ x: null, y: null });
  const dragLatestRef = useRef(null);
  const dragActiveRef = useRef(false);
  const nodeChangeFrameRef = useRef(0);
  const pendingNodeChangesRef = useRef(new Map());
  const connectionStartRef = useRef(null);
  const movementReasonsRef = useRef(new Set());
  const displayNodeCacheRef = useRef(new Map());
  const { screenToFlowPosition, fitView, zoomIn, zoomOut, setViewport, setCenter } = useReactFlow();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [ready, setReady] = useState(false);
  const [saveState, setSaveState] = useState('saved');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsHover, setSettingsHover] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [taskMonitorOpen, setTaskMonitorOpen] = useState(false);
  const [materialDraft, setMaterialDraft] = useState(null);
  const [materialBusy, setMaterialBusy] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const [contextMenu, setContextMenu] = useState(null);
  const [guides, setGuides] = useState({ x: null, y: null });
  useEffect(() => {
    if (!uploadOpen && !settingsOpen) return undefined;
    const closePanels = (event) => {
      if (uploadOpen && !event.target.closest?.('.toolbar-upload')) setUploadOpen(false);
      if (settingsOpen && !event.target.closest?.('.canvas-favorites-zone')) setSettingsOpen(false);
    };
    document.addEventListener('pointerdown', closePanels, true);
    return () => document.removeEventListener('pointerdown', closePanels, true);
  }, [settingsOpen, uploadOpen]);
  const [arranging, setArranging] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [viewportVersion, setViewportVersion] = useState(0);
  const [saveRevision, setSaveRevision] = useState(0);
  const [selectionAction, setSelectionAction] = useState(null);
  const [minimapPulseNodeId] = useState('');
  const batchConnectionRef = useRef(null);
  const selectionGestureRef = useRef(null);
  // React Flow emits onPaneClick after a completed lasso in some pointer
  // paths. Keep one hand-off bit so that click cleanup cannot erase the
  // persistent selection box that was just calculated by onSelectionEnd.
  const selectionCompletedRef = useRef(false);
  // React Flow's selection drag and viewport pan share the same pointer
  // stream. Keep the pointer-down phase separate from onSelectionStart so a
  // pan callback cannot clear a selection that has not crossed the drag
  // threshold yet.
  const selectionPointerDownRef = useRef(false);
  const [batchConnectionDrag, setBatchConnectionDrag] = useState(null);
  const [looseConnection, setLooseConnection] = useState(null);
  const [highlightedNodeId, setHighlightedNodeId] = useState('');
  const [, setFavoritesUploadLock] = useState(false);

  const looseConnectionCapabilities = useMemo(() => {
    if (!looseConnection) return { image: false, video: false };
    const ids = new Set(looseConnection.sourceIds?.length ? looseConnection.sourceIds : [looseConnection.sourceId]);
    const sources = nodes.filter((node) => ids.has(node.id));
    return {
      image: sources.some((node) => isConnectionSource(node, 'imageGenerator')),
      video: sources.some((node) => isConnectionSource(node, 'videoGenerator')),
    };
  }, [looseConnection, nodes]);

  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);

  const updateNodesState = useCallback((updater) => {
    setNodes((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      nodesRef.current = next;
      return next;
    });
  }, []);

  const updateEdgesState = useCallback((updater) => {
    setEdges((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      edgesRef.current = next;
      return next;
    });
  }, []);

  const markDirty = useCallback(() => setSaveRevision((value) => value + 1), []);

  const focusCreatedNode = useCallback((node) => {
    if (!node?.id) return;
    // Sidebar-created nodes should be brought into view without changing the
    // user's working scale. fitView made a newly-created node appear tiny by
    // zooming out to an artificial bounding box.
    const currentZoom = Number(viewportRef.current?.zoom) || 1;
    const width = Number(node.measured?.width || node.width || node.data?.width || node.data?.style?.width || 0);
    const height = Number(node.measured?.height || node.height || node.data?.height || node.data?.style?.height || 0);
    const centerX = Number(node.position?.x || 0) + width / 2;
    const centerY = Number(node.position?.y || 0) + height / 2;
    setCenter(centerX, centerY, { zoom: currentZoom, duration: 420 });
  }, [setCenter]);

  const clearSelectionOverlay = useCallback(() => {
    const root = wrapperRef.current;
    if (!root) return;
    // React Flow creates the live selection rectangle before firing
    // onSelectionStart. Do not hide/remove it while the gesture is active;
    // this cleanup is only for stale rectangles left after a gesture ends.
    if (selectionPointerDownRef.current || selectionGestureRef.current) return;
    const selectors = '.react-flow__selection, .react-flow__nodesselection-rect';
    root.querySelectorAll(selectors).forEach((selection) => {
      selection.classList.add('canvas-selection-stale');
    });
    // React Flow may retain the transient rectangle for one more paint. Keep
    // it hidden for that frame, but do not remove its DOM node while React
    // Flow still owns the pointer gesture; removing it mid-event can abort
    // the page layer and is the source of the rare disappearing-canvas bug.
    window.requestAnimationFrame(() => {
      // A new selection can be created in the same frame as this stale-node
      // cleanup. Never remove the live rectangle of that new gesture.
      if (selectionPointerDownRef.current || selectionGestureRef.current) return;
      // React Flow will unmount the transient element itself. Keeping it
      // hidden avoids mutating its live DOM during pointerup/contextmenu.
      root.querySelectorAll(selectors).forEach((selection) => {
        selection.classList.add('canvas-selection-stale');
      });
    });
  }, []);

  const clearActiveSelection = useCallback(() => {
    // A viewport pan is a new interaction context. Clear both React Flow's
    // selected flags and our persistent selection affordances together so a
    // stale box cannot be painted back by the selection effect.
    updateNodesState((current) => current.some((node) => node.selected)
      ? current.map((node) => node.selected ? { ...node, selected: false } : node)
      : current);
    updateEdgesState((current) => current.some((edge) => edge.selected)
      ? current.map((edge) => edge.selected ? { ...edge, selected: false } : edge)
      : current);
    selectionGestureRef.current = null;
    selectionPointerDownRef.current = false;
    selectionCompletedRef.current = false;
    setSelectionAction(null);
    setLooseConnection(null);
    clearSelectionOverlay();
  }, [clearSelectionOverlay, updateEdgesState, updateNodesState]);

  // React Flow normally owns the pointer stream, but a lasso can leave the
  // pane before release (for example when the cursor crosses a node or the
  // minimap). Capture the terminal event at window level so the next right
  // click/pan can never inherit a live selection gesture or ghost rectangle.
  useEffect(() => {
    const finishSelectionPointer = (event) => {
      if (event.button !== 0 || !selectionPointerDownRef.current) return;
      selectionPointerDownRef.current = false;
      selectionGestureRef.current = null;
      window.requestAnimationFrame(clearSelectionOverlay);
    };
    window.addEventListener('pointerup', finishSelectionPointer, true);
    window.addEventListener('pointercancel', finishSelectionPointer, true);
    return () => {
      window.removeEventListener('pointerup', finishSelectionPointer, true);
      window.removeEventListener('pointercancel', finishSelectionPointer, true);
    };
  }, [clearSelectionOverlay]);

  const beginMovement = useCallback((reason) => {
    movementReasonsRef.current.add(reason);
    wrapperRef.current?.classList.add('is-canvas-moving');
  }, []);

  const endMovement = useCallback((reason) => {
    movementReasonsRef.current.delete(reason);
    if (!movementReasonsRef.current.size) wrapperRef.current?.classList.remove('is-canvas-moving');
  }, []);

  const notify = useCallback((message, type = 'info') => onNotify?.(message, type), [onNotify]);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    updateNodesState([]);
    updateEdgesState([]);
    historyRef.current = { undo: [], redo: [] };
    setHistoryVersion((value) => value + 1);
    api.canvas(project.id).then((result) => {
      if (cancelled) return;
      const state = result.state || {};
      const normalizedNodes = (Array.isArray(state.nodes) ? state.nodes : []).map((node) => {
        const isUnusedLegacyImageGenerator = node.type === 'imageGenerator'
          && !node.data?.taskId
          && !String(node.data?.prompt || '').trim()
          && !(node.data?.results || []).length;
        return isUnusedLegacyImageGenerator
          ? { ...node, data: { ...node.data, count: 1 } }
          : node;
      });
      const loadedNodes = materializePersistedImageOutputs(normalizedNodes);
      const loadedEdges = Array.isArray(state.edges) ? state.edges : [];
      const loadedViewport = state.viewport || { x: 0, y: 0, zoom: 1 };
      updateNodesState(loadedNodes);
      updateEdgesState(loadedEdges);
      viewportRef.current = loadedViewport;
      setReady(true);
      requestAnimationFrame(() => {
        if (loadedNodes.length) setViewport(loadedViewport, { duration: 0 });
        else setViewport({ x: 0, y: 0, zoom: 1 }, { duration: 0 });
      });
    }).catch((error) => {
      if (!cancelled) { notify(error.message, 'error'); setReady(true); }
    });
    return () => { cancelled = true; window.clearTimeout(saveTimerRef.current); };
  }, [notify, project.id, setViewport, updateEdgesState, updateNodesState]);

  const saveNow = useCallback(async () => {
    if (!ready || nodesRef.current.some((node) => node.data?.uploading)) return;
    const sequence = ++saveSequenceRef.current;
    setSaveState('saving');
    try {
      await api.saveCanvas(project.id, serializableCanvas(nodesRef.current, edgesRef.current, viewportRef.current));
      if (sequence === saveSequenceRef.current) setSaveState('saved');
    } catch (error) {
      if (sequence === saveSequenceRef.current) setSaveState('error');
      notify(error.message, 'error');
    }
  }, [notify, project.id, ready]);

  useEffect(() => {
    if (!ready || saveRevision === 0 || nodesRef.current.some((node) => node.data?.uploading)) return undefined;
    setSaveState('saving');
    window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(saveNow, 1000);
    return () => window.clearTimeout(saveTimerRef.current);
  }, [ready, saveNow, saveRevision]);

  const snapshot = useCallback(() => ({
    nodes: structuredClone(nodesRef.current),
    edges: structuredClone(edgesRef.current),
  }), []);

  const checkpoint = useCallback((state = snapshot()) => {
    historyRef.current.undo.push(state);
    if (historyRef.current.undo.length > 60) historyRef.current.undo.shift();
    historyRef.current.redo = [];
    setHistoryVersion((value) => value + 1);
  }, [snapshot]);

  const restore = useCallback((state) => {
    updateNodesState(state.nodes);
    updateEdgesState(state.edges);
    markDirty();
    setHistoryVersion((value) => value + 1);
  }, [markDirty, updateEdgesState, updateNodesState]);

  const undo = useCallback(() => {
    const previous = historyRef.current.undo.pop();
    if (!previous) return;
    historyRef.current.redo.push(snapshot());
    restore(previous);
  }, [restore, snapshot]);

  const redo = useCallback(() => {
    const next = historyRef.current.redo.pop();
    if (!next) return;
    historyRef.current.undo.push(snapshot());
    restore(next);
  }, [restore, snapshot]);

  const updateNode = useCallback((id, patch) => {
    updateNodesState((current) => current.map((node) => node.id === id ? {
      ...node, data: { ...node.data, ...patch, updatedAt: Date.now() },
    } : node));
    markDirty();
  }, [markDirty, updateNodesState]);

  const renameAsset = useCallback(async (nodeId) => {
    const node = nodesRef.current.find((item) => item.id === nodeId);
    if (!node?.data?.assetId || !node.data.name?.trim()) return;
    try { await api.renameCanvasAsset(node.data.assetId, node.data.name.trim()); }
    catch (error) { notify(error.message, 'error'); }
  }, [notify]);

  const addReference = useCallback((source, target) => {
    const sourceNode = nodesRef.current.find((node) => node.id === source);
    const targetNode = nodesRef.current.find((node) => node.id === target);
    const option = sourceConnectionOption(sourceNode, targetNode?.type || '');
    if (!option || !targetNode || !['imageGenerator', 'videoGenerator'].includes(targetNode.type)) return false;
    if (option.audio && targetNode.type !== 'videoGenerator') return false;
    if (edgesRef.current.some((edge) => edge.source === source && edge.target === target)) return false;
    const targetModel = targetNode.type === 'imageGenerator'
      ? (config.models || []).find((item) => item.id === targetNode.data?.model)
      : (config.video?.models || []).find((item) => item.id === targetNode.data?.model);
    const maxReferences = targetNode.type === 'imageGenerator'
      ? Number(targetModel?.maxReferenceImages ?? config.generation?.maxReferenceImages ?? 9)
      : Number(targetModel?.maxReferenceImages ?? config.video?.maxReferenceImages ?? 9);
    const incoming = edgesRef.current.filter((edge) => edge.target === target);
    if (incoming.length >= maxReferences) { notify(`最多连接 ${maxReferences} 张参考图`, 'error'); return false; }
    if (targetNode.type === 'imageGenerator') {
      const model = targetModel;
      if (model?.supportsReferenceImages === false) { notify('当前图片模型不支持参考图', 'error'); return false; }
    }
    checkpoint();
    updateEdgesState((current) => [...current, {
      id: uid('reference'), source, target, type: 'reference',
      data: { order: Math.max(-1, ...incoming.map((edge) => Number(edge.data?.order ?? -1))) + 1, outputIndex: option.outputIndex || 0, createdAt: Date.now() },
    }]);
    markDirty();
    return true;
  }, [checkpoint, config, markDirty, notify, updateEdgesState]);

  const removeReference = useCallback((source, target) => {
    checkpoint();
    const ordered = edgesRef.current.filter((edge) => edge.target === target).sort((a, b) => Number(a.data?.order || 0) - Number(b.data?.order || 0));
    const removedSequence = ordered.findIndex((edge) => edge.source === source) + 1;
    updateEdgesState((current) => current.filter((edge) => !(edge.source === source && edge.target === target)));
    updateNodesState((current) => current.map((node) => node.id === target ? {
      ...node,
      data: {
        ...node.data,
        prompt: removedSequence > 0 ? String(node.data?.prompt || '').replace(/@图片(\d+)/gu, (token, value) => {
          const sequence = Number(value);
          return sequence === removedSequence ? '' : sequence > removedSequence ? `@图片${sequence - 1}` : token;
        }).replace(/\s{2,}/gu, ' ').trim() : node.data?.prompt,
        mentionedReferenceIds: (node.data?.mentionedReferenceIds || []).filter((id) => id !== source),
      },
    } : node));
    markDirty();
  }, [checkpoint, markDirty, updateEdgesState, updateNodesState]);

  const deleteReferenceEdge = useCallback((edgeId) => {
    const edge = edgesRef.current.find((item) => item.id === edgeId);
    if (!edge) return;
    checkpoint();
    const ordered = edgesRef.current.filter((item) => item.target === edge.target).sort((a, b) => Number(a.data?.order || 0) - Number(b.data?.order || 0));
    const removedSequence = ordered.findIndex((item) => item.id === edgeId) + 1;
    updateEdgesState((current) => current.filter((edge) => edge.id !== edgeId));
    updateNodesState((current) => current.map((node) => node.id === edge.target ? {
      ...node,
      data: {
        ...node.data,
        prompt: removedSequence > 0 ? String(node.data?.prompt || '').replace(/@图片(\d+)/gu, (token, value) => {
          const sequence = Number(value);
          return sequence === removedSequence ? '' : sequence > removedSequence ? `@图片${sequence - 1}` : token;
        }).replace(/\s{2,}/gu, ' ').trim() : node.data?.prompt,
        mentionedReferenceIds: (node.data?.mentionedReferenceIds || []).filter((id) => id !== edge.source),
      },
    } : node));
    markDirty();
  }, [checkpoint, markDirty, updateEdgesState, updateNodesState]);

  const deleteSelectedEdges = useCallback((edgeIds = []) => {
    const ids = new Set(edgeIds.filter(Boolean));
    if (!ids.size) return;
    checkpoint();
    updateEdgesState((current) => current.filter((edge) => !ids.has(edge.id)));
    updateNodesState((current) => current.map((node) => ({
      ...node,
      data: {
        ...node.data,
        mentionedReferenceIds: (node.data?.mentionedReferenceIds || []).filter((sourceId) => ![...ids].some((id) => {
          const edge = edgesRef.current.find((item) => item.id === id);
          return edge?.target === node.id && edge?.source === sourceId;
        })),
      },
    })));
    markDirty();
    setSelectionAction(null);
  }, [checkpoint, markDirty, updateEdgesState, updateNodesState]);

  const mentionReference = useCallback((sourceId, targetId) => {
    const target = nodesRef.current.find((node) => node.id === targetId);
    const connected = edgesRef.current.some((edge) => edge.source === sourceId && edge.target === targetId);
    if (!target || !connected) return;
    const current = Array.isArray(target.data?.mentionedReferenceIds) ? target.data.mentionedReferenceIds : [];
    if (!current.includes(sourceId)) updateNode(targetId, { mentionedReferenceIds: [...current, sourceId] });
  }, [updateNode]);

  const removeMention = useCallback((sourceId, targetId) => {
    const target = nodesRef.current.find((node) => node.id === targetId);
    const current = Array.isArray(target?.data?.mentionedReferenceIds) ? target.data.mentionedReferenceIds : [];
    updateNode(targetId, { mentionedReferenceIds: current.filter((id) => id !== sourceId) });
  }, [updateNode]);

  const makeReferenceFiles = useCallback(async (targetId, { nodeType = '', generationMode = '' } = {}) => {
    const normalizedGenerationMode = String(generationMode || '').trim().toLowerCase();
    if (nodeType === 'videoGenerator' && normalizedGenerationMode === 'text_to_video') return [];
    const seenSources = new Set();
    const incoming = edgesRef.current
      .filter((edge) => edge.target === targetId)
      .sort((a, b) => Number(a.data?.order || 0) - Number(b.data?.order || 0))
      .filter((edge) => {
        if (seenSources.has(edge.source)) return false;
        seenSources.add(edge.source);
        return true;
      });
    const candidates = incoming.map((edge) => {
      const node = nodesRef.current.find((item) => item.id === edge.source);
      return { edge, option: sourceOption(node, edge.data?.outputIndex) };
    });
    if (nodeType === 'videoGenerator' && ['frame_to_video', 'first_last_frame'].includes(normalizedGenerationMode)) {
      if (candidates.length !== 2 || candidates.some(({ option }) => option?.kind !== 'image')) {
        throw new Error('首尾帧模式需要按连线顺序连接首帧和尾帧两张图片。');
      }
    }
    return Promise.all(candidates.map(async ({ edge, option }, index) => {
      if (!option?.url) throw new Error('参考素材暂不可用');
      const response = await fetch(option.url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('参考素材读取失败');
      const blob = await response.blob();
      const expectedPrefix = option.kind === 'video' ? 'video/' : option.kind === 'audio' ? 'audio/' : 'image/';
      if (!blob.type.startsWith(expectedPrefix)) throw new Error('参考素材类型与文件内容不一致');
      const extension = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : blob.type.includes('mp4') ? 'mp4' : blob.type.includes('webm') ? 'webm' : blob.type.includes('mpeg') ? 'mp3' : blob.type.includes('wav') ? 'wav' : 'bin';
      const label = option.kind === 'video' ? '视频' : option.kind === 'audio' ? '音频' : '图片';
      return { file: new File([blob], `${label}${index + 1}.${extension}`, { type: blob.type }), sourceId: edge.source };
    })).then((items) => items.filter(Boolean));
  }, []);

  const generateNode = useCallback(async (nodeId) => {
    const node = nodesRef.current.find((item) => item.id === nodeId);
    if (!node?.data?.prompt?.trim()) { notify('请先输入提示词', 'error'); return; }
    updateNode(nodeId, { status: 'queued', progress: 2, message: '正在准备参考素材' });
    try {
      const references = await makeReferenceFiles(nodeId, {
        nodeType: node.type,
        generationMode: node.type === 'videoGenerator' ? node.data.generationMode || '' : '',
      });
      const request = {
        // @ only identifies an already connected reference in the editor. Files are
        // collected from unique incoming edges, so an @ token must never add it twice.
        prompt: node.data.prompt.trim(), model: node.data.model, ratio: node.data.ratio,
        projectId: project.id, canvasNodeId: nodeId, references,
      };
      const result = node.type === 'imageGenerator'
        ? await api.generate({ ...request, resolution: node.data.resolution, count: node.data.count, stylePreset: '' })
        : await api.generateVideo({
          ...request,
          resolution: node.data.resolution || '',
          duration: Number(node.data.duration || 5),
          generationMode: node.data.generationMode || '',
        });
      updateNode(nodeId, {
        taskId: result.task.id,
        status: result.task.status || result.task.state || 'queued',
        progress: result.task.progress || 4,
        message: result.task.message || '任务已进入队列',
      });
      await onSessionRefresh?.();
    } catch (error) {
      updateNode(nodeId, { status: 'failed', progress: 0, message: error.message });
      notify(error.message, 'error');
    }
  }, [makeReferenceFiles, notify, onSessionRefresh, project.id, updateNode]);

  const activeTaskKey = nodes
    .filter((node) => node.data?.taskId && ACTIVE_STATES.has(node.data?.status))
    .map((node) => `${node.id}:${node.data.taskId}:${node.type}`)
    .join('|');

  useEffect(() => {
    const active = nodesRef.current.filter((node) => node.data?.taskId && ACTIVE_STATES.has(node.data?.status));
    if (!active.length) return undefined;
    let stopped = false;
    const reportedPollErrors = new Set();
    const poll = async () => {
      const results = await Promise.allSettled(active.map((node) => node.type === 'imageGenerator' ? api.task(node.data.taskId) : api.videoTask(node.data.taskId)));
      if (stopped) return;
      let reachedTerminal = false;
      let changed = false;
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const key = `${active[index].id}:${active[index].data.taskId}`;
          if (!reportedPollErrors.has(key)) {
            reportedPollErrors.add(key);
            notify('任务状态暂时无法读取，正在自动重试', 'error');
          }
        }
      });
      updateNodesState((current) => {
        let next = current.map((node) => {
          const index = active.findIndex((item) => item.id === node.id);
          if (index < 0 || results[index].status !== 'fulfilled') return node;
          const task = results[index].value.task || results[index].value;
          const status = task.status || task.state || node.data.status;
          if (!ACTIVE_STATES.has(status)) reachedTerminal = true;
          const images = taskResults(task);
          const requestedCount = Math.max(1, Number(node.data?.count) || 1);
          const partialImages = node.type === 'imageGenerator'
            && requestedCount > 1
            && images.length > 0
            && images.length < requestedCount
            && !ACTIVE_STATES.has(status);
          const taskMessage = partialImages
            ? `${task.message ? `${task.message}；` : ''}已生成 ${images.length}/${requestedCount} 张，部分失败`
            : task.message || '';
          const nextData = node.type === 'imageGenerator' ? {
            ...node.data, status, progress: Number(task.progress || 0), message: taskMessage,
            results: images, activeResult: Math.min(Number(node.data.activeResult || 0), Math.max(0, images.length - 1)),
          } : {
            ...node.data, status, progress: Number(task.progress || 0), message: task.message || '',
            videoUrl: task.videoUrl || '', coverUrl: task.coverUrl || '',
          };
          const tracked = node.type === 'imageGenerator'
            ? ['status', 'progress', 'message', 'videoUrl', 'coverUrl', 'results', 'activeResult']
            : ['status', 'progress', 'message', 'videoUrl', 'coverUrl'];
          const nodeChanged = tracked.some((key) => JSON.stringify(node.data?.[key]) !== JSON.stringify(nextData[key]));
          if (nodeChanged) changed = true;
          return nodeChanged ? { ...node, data: { ...nextData, updatedAt: Date.now() } } : node;
        });
        active.forEach((activeNode, index) => {
          if (activeNode.type !== 'imageGenerator' || results[index].status !== 'fulfilled') return;
          const task = results[index].value.task || results[index].value;
          const status = task.status || task.state || activeNode.data.status;
          const images = taskResults(task);
          if (ACTIVE_STATES.has(status) || !images.length) return;
          const generator = next.find((node) => node.id === activeNode.id);
          const before = next.length;
          next = materializeImageOutputs(next, generator, images);
          if (next.length !== before) changed = true;
        });
        return next;
      });
      if (changed) markDirty();
      if (reachedTerminal) await onSessionRefresh?.();
    };
    void poll();
    const timer = window.setInterval(poll, 2400);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [activeTaskKey, markDirty, notify, onSessionRefresh, updateNodesState]);

  const openPicker = useCallback((kind, position = null, nodeId = '') => {
    pendingPickerRef.current = { kind, position, nodeId };
    if (fileInputRef.current) {
      fileInputRef.current.accept = kind === 'image' ? 'image/jpeg,image/png,image/webp' : kind === 'video' ? 'video/mp4,video/webm,video/quicktime' : 'audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg';
      fileInputRef.current.multiple = !nodeId;
      fileInputRef.current.click();
    }
    setUploadOpen(false);
    setContextMenu(null);
  }, []);

  const visibleCanvasBounds = useCallback(() => {
    const rect = wrapperRef.current.getBoundingClientRect();
    const topLeft = screenToFlowPosition({ x: rect.left + (settingsOpen || settingsHover ? 470 : 82), y: rect.top + 76 });
    const bottomRight = screenToFlowPosition({ x: rect.right - 24, y: rect.bottom - 58 });
    return {
      x: Math.min(topLeft.x, bottomRight.x),
      y: Math.min(topLeft.y, bottomRight.y),
      width: Math.abs(bottomRight.x - topLeft.x),
      height: Math.abs(bottomRight.y - topLeft.y),
    };
  }, [screenToFlowPosition, settingsHover, settingsOpen]);

  const addPlacedNode = useCallback((draft, requestedCenter = null, useAnchor = true) => {
    const rect = wrapperRef.current.getBoundingClientRect();
    const bounds = visibleCanvasBounds();
    const desiredCenter = requestedCenter || screenToFlowPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
    const visibleNodes = nodesRef.current.filter((node) => {
      const size = nodeSize(node);
      return node.position.x < bounds.x + bounds.width && node.position.x + size.width > bounds.x
        && node.position.y < bounds.y + bounds.height && node.position.y + size.height > bounds.y;
    });
    const selectedAnchor = nodesRef.current.find((node) => node.selected) || null;
    const anchor = useAnchor
      ? (selectedAnchor || [...visibleNodes].sort((left, right) => Number(right.data?.createdAt || 0) - Number(left.data?.createdAt || 0))[0] || null)
      : null;
    const placement = findVisiblePosition(nodesRef.current, desiredCenter, nodeSize(draft), {
      bounds,
      anchor: requestedCenter ? null : anchor,
      gap: 88,
    });
    draft.position = placement.position;
    draft.selected = true;
    updateNodesState((current) => [...current.map((node) => node.selected ? { ...node, selected: false } : node), draft]);
    markDirty();
    if (!placement.fitsViewport) {
      window.setTimeout(() => fitView({
        nodes: [anchor, draft].filter(Boolean).map((node) => ({ id: node.id })),
        padding: 0.18,
        duration: 320,
        maxZoom: 1,
      }), 20);
    }
    return draft;
  }, [fitView, markDirty, screenToFlowPosition, updateNodesState, visibleCanvasBounds]);

  const addStackedNode = useCallback((draft, requestedPosition = null) => {
    if (requestedPosition) return addPlacedNode(draft, requestedPosition, false);
    const sameType = [...nodesRef.current]
      .filter((node) => node.type === draft.type && (draft.type !== 'media' || node.data?.kind === draft.data?.kind))
      .sort((left, right) => Number(right.data?.createdAt || 0) - Number(left.data?.createdAt || 0));
    const anchor = nodesRef.current.find((node) => node.selected) || sameType[0] || null;
    if (!anchor) return addPlacedNode(draft, null, false);
    draft.position = duplicatePosition(anchor, nodesRef.current, 76);
    draft.selected = true;
    updateNodesState((current) => [...current.map((node) => node.selected ? { ...node, selected: false } : node), draft]);
    markDirty();
    return draft;
  }, [addPlacedNode, markDirty, updateNodesState]);

  const uploadFiles = useCallback(async (inputFiles, forcedKind, position, targetNodeId = '') => {
    const files = [...(inputFiles || [])].filter((file) => fileKind(file) === forcedKind);
    if (!files.length) { notify('文件类型不符合当前素材类型', 'error'); return; }
    checkpoint();
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      let metadata = { width: 0, height: 0, duration: 0 };
      try { metadata = await readMediaMetadata(file, forcedKind); } catch { /* keep a usable file node */ }
      const localUrl = forcedKind === 'image' ? '' : URL.createObjectURL(file);
      const temporaryId = targetNodeId || uid(`${forcedKind}-upload`);
      if (targetNodeId) {
        updateNode(targetNodeId, { kind: forcedKind, name: pathName(file.name), url: localUrl, ...metadata, uploading: true });
      } else {
        const placed = addPlacedNode({
          id: temporaryId, type: 'media', position: { x: 0, y: 0 },
          data: { kind: forcedKind, name: pathName(file.name), url: localUrl, ...metadata, uploading: true, createdAt: Date.now(), updatedAt: Date.now() },
        }, position ? { x: position.x + index * 44, y: position.y + index * 44 } : null, true);
        if (!position && index === 0) focusCreatedNode(placed);
      }
      try {
        const result = await api.uploadCanvasAsset({ projectId: project.id, file, name: pathName(file.name), ...metadata });
        if (localUrl) URL.revokeObjectURL(localUrl);
        updateNode(temporaryId, { ...result.asset, assetId: result.asset.id, url: result.asset.url, uploading: false });
      } catch (error) {
        updateNode(temporaryId, { uploading: false, message: error.message });
        notify(error.message, 'error');
      }
    }
  }, [addPlacedNode, checkpoint, focusCreatedNode, notify, project.id, updateNode]);

  const addGenerator = useCallback((kind, position) => {
    checkpoint();
    const draft = createGeneratorNode(kind, { x: 0, y: 0 }, config);
    const placed = addStackedNode(draft, position);
    if (!position) focusCreatedNode(placed);
    setContextMenu(null);
  }, [addStackedNode, checkpoint, config, focusCreatedNode]);

  const createConnectedAt = useCallback((kind, sourceId, position) => {
    const targetType = kind === 'video' ? 'videoGenerator' : 'imageGenerator';
    const explicitIds = new Set(looseConnection?.sourceIds || []);
    const selectedSources = nodesRef.current
      .filter((node) => (explicitIds.size ? explicitIds.has(node.id) : node.selected)
        && isConnectionSource(node, targetType));
    const sources = selectedSources.some((node) => node.id === sourceId)
      ? selectedSources
      : nodesRef.current.filter((node) => node.id === sourceId && sourceConnectionOption(node, targetType));
    if (!sources.length) return;
    checkpoint();
    const draft = createGeneratorNode(kind, { x: 0, y: 0 }, config);
    draft.position = { x: Math.round(position.x), y: Math.round(position.y) };
    draft.selected = true;
    updateNodesState((current) => [...current.map((node) => ({ ...node, selected: false })), draft]);
    updateEdgesState((current) => [...current, ...sources.map((source, index) => ({
      id: uid('reference'), source: source.id, target: draft.id, type: 'reference',
      data: { order: index, outputIndex: sourceOption(source)?.outputIndex || 0, createdAt: Date.now() },
    }))]);
    markDirty();
    setLooseConnection(null);
  }, [checkpoint, config, looseConnection, markDirty, updateEdgesState, updateNodesState]);

  const startBatchConnection = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const sources = nodesRef.current.filter((node) => node.selected && sourceConnectionOption(node, 'videoGenerator'));
    if (sources.length < 2 || batchConnectionRef.current) return;
    const wrapper = wrapperRef.current;
    const buttonRect = event.currentTarget?.getBoundingClientRect();
    const wrapperRect = wrapper?.getBoundingClientRect();
    if (!wrapperRect || !buttonRect) return;
    const sourceIds = sources.map((node) => node.id);
    const start = {
      x: buttonRect.left + buttonRect.width / 2 - wrapperRect.left,
      y: buttonRect.top + buttonRect.height / 2 - wrapperRect.top,
    };
    batchConnectionRef.current = { sourceIds };
    setBatchConnectionDrag({ sourceIds, start, end: start });
    const onMove = (moveEvent) => {
      setBatchConnectionDrag((current) => current ? ({ ...current, end: { x: moveEvent.clientX - wrapperRect.left, y: moveEvent.clientY - wrapperRect.top } }) : current);
    };
    const onUp = (upEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const current = batchConnectionRef.current;
      batchConnectionRef.current = null;
      setBatchConnectionDrag(null);
      if (!current) return;
      const target = nodesRef.current.find((node) => ['imageGenerator', 'videoGenerator'].includes(node.type) && (() => {
        const rect = wrapper.querySelector(`[data-id="${node.id}"]`)?.getBoundingClientRect();
        return rect && upEvent.clientX >= rect.left && upEvent.clientX <= rect.right && upEvent.clientY >= rect.top && upEvent.clientY <= rect.bottom;
      })());
      if (target) {
        current.sourceIds.forEach((id) => {
          const source = nodesRef.current.find((node) => node.id === id);
          if (source && sourceConnectionOption(source, target.type)) addReference(id, target.id);
        });
        return;
      }
      const position = screenToFlowPosition({ x: upEvent.clientX, y: upEvent.clientY });
      setLooseConnection({
        sourceId: current.sourceIds[0], sourceIds: current.sourceIds,
        position,
        x: Math.max(12, Math.min(wrapperRect.width - 232, upEvent.clientX - wrapperRect.left + 14)),
        y: Math.max(12, Math.min(wrapperRect.height - 138, upEvent.clientY - wrapperRect.top - 54)),
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
  }, [addReference, screenToFlowPosition]);

  useEffect(() => {
    const selected = nodes.filter((node) => node.selected);
    const selectedSources = selected.filter((node) => isConnectionSource(node, 'videoGenerator'));
    if (selected.length < 2 || !wrapperRef.current) {
      setSelectionAction((current) => current?.edgeIds?.length ? current : null);
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      const rects = selected
        .map((node) => wrapperRef.current?.querySelector(`[data-id="${node.id}"]`)?.getBoundingClientRect())
        .filter(Boolean);
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      if (!rects.length || !wrapperRect) return;
      const left = Math.min(...rects.map((rect) => rect.left));
      const right = Math.max(...rects.map((rect) => rect.right));
      const top = Math.min(...rects.map((rect) => rect.top));
      const bottom = Math.max(...rects.map((rect) => rect.bottom));
      const box = {
        x: Math.max(0, left - wrapperRect.left - 8),
        y: Math.max(0, top - wrapperRect.top - 8),
        width: right - left + 16,
        height: bottom - top + 16,
      };
      const next = {
        x: Math.max(92, Math.min(wrapperRect.width - 210, right - wrapperRect.left + 20)),
        y: Math.max(88, Math.min(wrapperRect.height - 54, (top + bottom) / 2 - wrapperRect.top - 22)),
        count: selectedSources.length,
        box,
        selectedIds: selected.map((node) => node.id).sort(),
      };
      setSelectionAction((current) => {
        if (!current || current.edgeIds?.length) return next;
        const sameBox = current.box && ['x', 'y', 'width', 'height'].every((key) => Math.abs(Number(current.box[key]) - Number(box[key])) < 0.5);
        const sameIds = current.selectedIds?.length === next.selectedIds.length
          && current.selectedIds.every((id, index) => id === next.selectedIds[index]);
        return sameBox && sameIds && current.count === next.count
          ? current
          : next;
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [nodes, settingsOpen, settingsHover, viewportVersion]);

  const addEmptyAudio = useCallback((position) => {
    checkpoint();
    const draft = createMediaNode({ kind: 'audio', name: '音频素材', url: '' }, { x: 0, y: 0 });
    const placed = addStackedNode(draft, position);
    if (!position) focusCreatedNode(placed);
    setContextMenu(null);
  }, [addStackedNode, checkpoint, focusCreatedNode]);

  const addSettingMaterial = useCallback((material, position = null) => {
    checkpoint();
    const kind = ['image', 'video', 'audio'].includes(material.mediaType) ? material.mediaType
      : String(material.mimeType || '').startsWith('video/') ? 'video'
        : String(material.mimeType || '').startsWith('audio/') ? 'audio' : 'image';
    const draft = createMediaNode({
      kind,
      name: material.name || `${kind === 'image' ? '图片' : kind === 'video' ? '视频' : '音频'}收藏`,
      url: material.url,
      previewUrl: material.previewUrl || '',
      downloadUrl: material.downloadUrl || material.url,
      poster: material.poster || '',
      width: Number(material.width || 0),
      height: Number(material.height || 0),
      ratio: assetRatio(material, kind === 'video' ? '16:9' : '16:9'),
    }, { x: 0, y: 0 });
    draft.data.materialId = material.id;
    addPlacedNode(draft, position, true);
    notify('收藏素材已添加到画布');
  }, [addPlacedNode, checkpoint, notify]);

  const addLibraryAsset = useCallback((asset, name, position = null) => {
    checkpoint();
    const draft = createMediaNode({
      kind: asset.kind,
      name: String(name || '').trim() || asset.fallbackName || (asset.kind === 'video' ? '视频' : '图片'),
      url: asset.url,
      previewUrl: asset.previewUrl || '',
      downloadUrl: asset.downloadUrl || asset.url,
      poster: asset.poster || '',
      width: Number(asset.width || 0),
      height: Number(asset.height || 0),
      ratio: assetRatio(asset, asset.kind === 'video' ? '16:9' : '16:9'),
      duration: asset.duration || 0,
    }, { x: 0, y: 0 });
    draft.data.sourceTaskId = asset.taskId || '';
    draft.data.sourceIndex = Number(asset.imageIndex || 0);
    addPlacedNode(draft, position, !position);
    setLibraryOpen(false);
    notify(`${asset.kind === 'video' ? '视频' : '图片'}已加入画布`);
  }, [addPlacedNode, checkpoint, notify]);

  const addVideoFrame = useCallback((frame, ownerOrPosition = null) => {
    if (!frame?.url) return;
    checkpoint();
    const owner = typeof ownerOrPosition === 'string'
      ? nodesRef.current.find((node) => node.id === ownerOrPosition)
      : null;
    let targetNode = owner && ['imageGenerator', 'videoGenerator'].includes(owner.type) ? owner : null;
    if (owner?.type === 'media' && owner.data?.kind === 'video') {
      // A frame selected from a video material belongs to the generator that
      // consumes that material, when one exists. This keeps the new frame in
      // the same reference workflow instead of creating an unconnected node.
      const edge = edgesRef.current.find((item) => item.source === owner.id);
      targetNode = edge ? nodesRef.current.find((node) => node.id === edge.target && ['imageGenerator', 'videoGenerator'].includes(node.type)) : null;
    }
    const targetId = targetNode?.id || '';
    const draft = createMediaNode({
      kind: 'image',
      name: `视频帧 ${Number(frame.time || 0).toFixed(2)}s`,
      url: frame.url,
      previewUrl: frame.url,
      width: Number(frame.width || 16) || 16,
      height: Number(frame.height || 9) || 9,
    }, { x: 0, y: 0 });
    const frameSize = nodeSize(draft);
    const frameAnchorId = targetId || owner?.id || '';
    const linkedFrames = frameAnchorId
      ? nodesRef.current.filter((node) => node?.type === 'media'
        && node.data?.kind === 'image'
        && (node.data?.frameOwnerId === owner?.id || node.data?.frameTargetId === targetId))
      : [];
    const anchorNode = targetNode || owner;
    const requestedPosition = anchorNode
      ? {
        // addPlacedNode receives a center point. Keep captured frames in a
        // dedicated left lane, ordered from top to bottom.
        x: anchorNode.position.x - 56 - frameSize.width / 2,
        y: anchorNode.position.y + frameSize.height / 2 + linkedFrames.length * (frameSize.height + 58),
      }
      : ownerOrPosition;
    draft.data.frameOwnerId = owner?.id || '';
    draft.data.frameTargetId = targetId;
    draft.data.uploading = true;
    const placed = addPlacedNode(draft, requestedPosition, !requestedPosition);
    if (targetId) updateEdgesState((current) => current.some((edge) => edge.source === placed.id && edge.target === targetId)
      ? current
      : [...current, {
        id: uid('reference'), source: placed.id, target: targetId, type: 'reference',
        data: { order: current.filter((edge) => edge.target === targetId).length, outputIndex: 0, createdAt: Date.now() },
      }]);
    void (async () => {
      try {
        const response = await fetch(frame.url);
        if (!response.ok) throw new Error('视频帧读取失败');
        const blob = await response.blob();
        const extension = blob.type.includes('png') ? 'png' : 'jpg';
        const file = new File([blob], `video-frame-${Date.now()}.${extension}`, { type: blob.type || 'image/jpeg' });
        const result = await api.uploadCanvasAsset({ projectId: project.id, file, name: draft.data.name, width: draft.data.width, height: draft.data.height });
        updateNode(placed.id, { ...result.asset, assetId: result.asset.id, url: result.asset.url, previewUrl: result.asset.previewUrl || '', uploading: false });
        notify('视频帧已加入画布');
      } catch (error) {
        updateNode(placed.id, { uploading: false, message: error.message });
        notify(error.message, 'error');
      }
    })();
  }, [addPlacedNode, checkpoint, notify, project.id, updateEdgesState, updateNode]);

  const saveCanvasImageAsMaterial = useCallback(async (draft) => {
    if (!draft?.url || !draft.category || !draft.name) return;
    const kind = ['image', 'video', 'audio'].includes(draft.mediaType || draft.kind) ? (draft.mediaType || draft.kind) : 'image';
    setMaterialBusy(true);
    try {
      if (draft.taskId && kind === 'image') {
        await api.createMaterial({
          projectId: project.id,
          category: draft.category,
          mediaType: 'image',
          name: draft.name,
          taskId: draft.taskId,
          imageIndex: Number(draft.imageIndex || 0),
        });
      } else {
        const token = getToken();
        const response = await fetch(draft.url, {
          credentials: 'same-origin',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!response.ok) throw new Error('素材读取失败，请稍后重试');
        const blob = await response.blob();
        if (!blob.type.startsWith(`${kind}/`)) throw new Error('当前素材类型与收藏分类不一致');
        const extension = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp'
          : blob.type.includes('webm') ? 'webm' : blob.type.includes('quicktime') ? 'mov'
            : blob.type.includes('wav') ? 'wav' : blob.type.includes('ogg') ? 'ogg'
              : blob.type.includes('aac') ? 'aac' : kind === 'video' ? 'mp4' : kind === 'audio' ? 'mp3' : 'jpg';
        const file = new File([blob], `${draft.name}.${extension}`, { type: blob.type });
        await api.createMaterial({ projectId: project.id, category: draft.category, mediaType: kind, name: draft.name, file, source: 'canvas' });
      }
      await onSessionRefresh?.();
      setMaterialDraft(null);
      notify('素材已加入收藏夹');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setMaterialBusy(false);
    }
  }, [notify, onSessionRefresh, project.id]);

  const downloadMedia = useCallback((media) => {
    if (!media?.url) return;
    const anchor = document.createElement('a');
    const separator = media.url.includes('?') ? '&' : '?';
    anchor.href = media.downloadUrl || `${media.url}${separator}download=1`;
    anchor.download = `${String(media.name || (media.kind === 'video' ? '视频' : media.kind === 'audio' ? '音频' : '图片')).replace(/[\\/:*?"<>|]+/g, '_')}${media.kind === 'video' ? '.mp4' : media.kind === 'audio' ? '.mp3' : '.png'}`;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }, []);

  const deleteNodes = useCallback((ids) => {
    if (!ids.length) return;
    checkpoint();
    const selected = new Set(ids);
    updateNodesState((current) => current.filter((node) => !selected.has(node.id)));
    updateEdgesState((current) => current.filter((edge) => !selected.has(edge.source) && !selected.has(edge.target)));
    markDirty();
    setContextMenu(null);
  }, [checkpoint, markDirty, updateEdgesState, updateNodesState]);

  const duplicateNode = useCallback((id) => {
    const node = nodesRef.current.find((item) => item.id === id);
    if (!node) return;
    checkpoint();
    const copy = structuredClone(node);
    copy.id = uid(node.type);
    copy.position = duplicatePosition(node, nodesRef.current, 76);
    copy.selected = true;
    copy.data = {
      ...copy.data,
      taskId: '', status: 'idle', progress: 0, message: '', results: [], activeResult: 0,
      videoUrl: '', coverUrl: '', mentionedReferenceIds: [], createdAt: Date.now(), updatedAt: Date.now(),
    };
    const incoming = edgesRef.current.filter((edge) => edge.target === node.id).map((edge) => ({
      ...structuredClone(edge),
      id: uid('reference'),
      target: copy.id,
      data: { ...edge.data, createdAt: Date.now() },
    }));
    updateNodesState((current) => [...current.map((item) => item.selected ? { ...item, selected: false } : item), copy]);
    if (incoming.length) updateEdgesState((current) => [...current, ...incoming]);
    markDirty();
    setContextMenu(null);
  }, [checkpoint, markDirty, updateEdgesState, updateNodesState]);

  const deriveNode = useCallback((sourceId, kind) => {
    const source = nodesRef.current.find((node) => node.id === sourceId);
    const option = sourceOption(source);
    if (!source || !option) {
      notify('请先完成图片或生成图片，再派生下游节点', 'error');
      return;
    }
    checkpoint();
    const draft = createGeneratorNode(kind, { x: 0, y: 0 }, config);
    const placement = findVisiblePosition(nodesRef.current, { x: source.position.x, y: source.position.y }, nodeSize(draft), {
      anchor: source,
      gap: 120,
    });
    draft.position = placement.position;
    draft.selected = true;
    const incoming = edgesRef.current.filter((edge) => edge.target === draft.id);
    const edge = {
      id: uid('reference'), source: source.id, target: draft.id, type: 'reference',
      data: { order: incoming.length, outputIndex: option.outputIndex || 0, createdAt: Date.now() },
    };
    updateNodesState((current) => [...current.map((node) => node.selected ? { ...node, selected: false } : node), draft]);
    updateEdgesState((current) => [...current, edge]);
    markDirty();
    setContextMenu(null);
  }, [checkpoint, config, markDirty, notify, updateEdgesState, updateNodesState]);

  const arrange = useCallback(() => {
    if (!nodesRef.current.length) return;
    checkpoint();
    const rect = wrapperRef.current.getBoundingClientRect();
    const center = screenToFlowPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    setArranging(true);
    const arranged = arrangeCanvas(nodesRef.current, edgesRef.current, center);
    updateNodesState(arranged);
    markDirty();
    window.setTimeout(() => {
      const activeIds = arranged.filter((node) => node.type !== 'media' || edgesRef.current.some((edge) => edge.source === node.id || edge.target === node.id)).map((node) => ({ id: node.id }));
      fitView({ nodes: activeIds.length ? activeIds : undefined, padding: 0.17, duration: 520, maxZoom: 1.05 });
      setArranging(false);
    }, 380);
    setContextMenu(null);
  }, [checkpoint, fitView, markDirty, screenToFlowPosition, updateNodesState]);

  const pickForNode = useCallback((id, kind) => openPicker(kind, null, id), [openPicker]);
  const nodeActions = useMemo(() => ({
    update: updateNode,
    renameAsset,
    generate: generateNode,
    addReference,
    removeReference,
    mentionReference,
    removeMention,
    pickForNode,
    duplicate: duplicateNode,
    derive: deriveNode,
    removeNode: (id) => deleteNodes([id]),
    preview: setMediaPreview,
    saveMaterial: setMaterialDraft,
    download: downloadMedia,
    notify,
    highlightReference: setHighlightedNodeId,
    addVideoFrame,
  }), [addReference, addVideoFrame, deleteNodes, deriveNode, downloadMedia, duplicateNode, generateNode, mentionReference, notify, pickForNode, removeMention, removeReference, renameAsset, updateNode]);

  const displayNodes = useMemo(() => {
    const byId = new Map(nodes.map((node) => [node.id, node]));
    const cache = displayNodeCacheRef.current;
    const liveIds = new Set(nodes.map((node) => node.id));
    cache.forEach((_value, id) => { if (!liveIds.has(id)) cache.delete(id); });
    const incomingByTarget = new Map();
    edges.forEach((edge) => {
      const incoming = incomingByTarget.get(edge.target) || [];
      incoming.push(edge);
      incomingByTarget.set(edge.target, incoming);
    });
    incomingByTarget.forEach((incoming) => incoming.sort((a, b) => Number(a.data?.order || 0) - Number(b.data?.order || 0)));
    const highlightedEdgeIds = new Set(selectionAction?.edgeIds || []);
    const highlightedNodeIds = new Set();
    if (highlightedEdgeIds.size) {
      edges.forEach((edge) => {
        if (highlightedEdgeIds.has(edge.id)) {
          highlightedNodeIds.add(edge.source);
          highlightedNodeIds.add(edge.target);
        }
      });
    }
    let imageSequence = 0;
    let videoSequence = 0;
    let audioSequence = 0;
    return nodes.map((node) => {
      let displayLabel = '';
      if (node.type === 'media' && node.data?.kind === 'image') {
        imageSequence += 1;
        displayLabel = readableAssetName(node.data?.name) || `图片${imageSequence}`;
      }
      if (node.type === 'media' && node.data?.kind === 'video') {
        videoSequence += 1;
        displayLabel = readableAssetName(node.data?.name) || `视频${videoSequence}`;
      }
      if (node.type === 'media' && node.data?.kind === 'audio') {
        audioSequence += 1;
        displayLabel = readableAssetName(node.data?.name) || `音频${audioSequence}`;
      }
      const incoming = incomingByTarget.get(node.id) || [];
      const referenceSequences = { image: 0, video: 0, audio: 0 };
      const references = incoming.map((edge) => {
        const option = sourceOption(byId.get(edge.source), edge.data?.outputIndex);
        if (!option) return null;
        const kind = option.kind || 'image';
        referenceSequences[kind] = (referenceSequences[kind] || 0) + 1;
        return { ...option, sequence: referenceSequences[kind] };
      }).filter(Boolean);
      const mentionedIds = new Set(Array.isArray(node.data?.mentionedReferenceIds) ? node.data.mentionedReferenceIds : []);
      const mentions = references.filter((reference) => mentionedIds.has(reference.id));
      const canDerive = Boolean(sourceOption(node));
      const referenceKey = JSON.stringify(references.map((item) => [item.id, item.url, item.previewUrl, item.outputIndex]));
      const edgeHighlighted = highlightedNodeIds.has(node.id);
      const decorationKey = `${referenceKey}:${JSON.stringify([...mentionedIds])}:${displayLabel}:${canDerive}:${highlightedNodeId === node.id}:${edgeHighlighted}`;
      const cached = cache.get(node.id);
      if (cached?.source === node && cached.decorationKey === decorationKey && cached.config === config && cached.actions === nodeActions) return cached.value;
      const value = {
        ...node,
        // Audio explicitly exposes its header and body as drag surfaces. This
        // prevents the empty upload target from swallowing React Flow's drag.
        dragHandle: node.type === 'media' && node.data?.kind === 'audio'
          ? '.audio-node-drag-surface'
          : node.dragHandle,
        data: {
          ...node.data,
          _config: config,
          _references: references,
          _mentions: mentions,
          _displayLabel: displayLabel,
          _canDerive: canDerive,
          _highlighted: highlightedNodeId === node.id,
          _edgeHighlighted: edgeHighlighted,
          _actions: nodeActions,
        },
      };
      cache.set(node.id, { source: node, decorationKey, config, actions: nodeActions, value });
      return value;
    });
  }, [config, edges, highlightedNodeId, nodeActions, nodes, selectionAction]);

  const displayEdges = useMemo(() => {
    const incomingCounts = new Map();
    edges.forEach((edge) => incomingCounts.set(edge.target, (incomingCounts.get(edge.target) || 0) + 1));
    return edges.map((edge) => ({
      ...edge,
      selected: Boolean(edge.selected || selectionAction?.edgeIds?.includes(edge.id)),
      data: { ...edge.data, incomingCount: incomingCounts.get(edge.target) || 1, _delete: deleteReferenceEdge },
    }));
  }, [deleteReferenceEdge, edges, selectionAction]);

  const flushPendingNodeChanges = useCallback(() => {
    if (nodeChangeFrameRef.current) {
      window.cancelAnimationFrame(nodeChangeFrameRef.current);
      nodeChangeFrameRef.current = 0;
    }
    const pending = pendingNodeChangesRef.current;
    if (!pending.size) return;
    pendingNodeChangesRef.current = new Map();
    updateNodesState((current) => applyNodeChanges([...pending.values()], current));
  }, [updateNodesState]);

  const schedulePendingNodeChanges = useCallback(() => {
    if (nodeChangeFrameRef.current) return;
    nodeChangeFrameRef.current = window.requestAnimationFrame(() => {
      nodeChangeFrameRef.current = 0;
      const pending = pendingNodeChangesRef.current;
      if (!pending.size) return;
      pendingNodeChangesRef.current = new Map();
      updateNodesState((current) => applyNodeChanges([...pending.values()], current));
    });
  }, [updateNodesState]);

  // React Flow already moves the visible DOM nodes during a drag. Coalesce
  // controlled position updates to one animation frame so multi-drag does not
  // rebuild the whole node decoration tree for every pointer event.
  const handleNodesChange = useCallback((changes) => {
    if (!dragActiveRef.current) {
      updateNodesState((current) => applyNodeChanges(changes, current));
      return;
    }
    const immediateChanges = [];
    let hasPositionChanges = false;
    changes.forEach((change) => {
      if (change.type === 'position') {
        hasPositionChanges = true;
        pendingNodeChangesRef.current.set(change.id, change);
      }
      else immediateChanges.push(change);
    });
    if (immediateChanges.length) updateNodesState((current) => applyNodeChanges(immediateChanges, current));
    if (hasPositionChanges) schedulePendingNodeChanges();
  }, [schedulePendingNodeChanges, updateNodesState]);

  const handleEdgesChange = useCallback((changes) => {
    updateEdgesState((current) => applyEdgeChanges(changes, current));
    if (changes.some((change) => change.type === 'remove')) markDirty();
  }, [markDirty, updateEdgesState]);

  const handleMove = useCallback((_event, nextViewport) => {
    viewportRef.current = nextViewport;
  }, []);

  const handleConnectStart = useCallback((_event, params) => {
    connectionStartRef.current = params;
    setLooseConnection(null);
  }, []);

  const handleConnect = useCallback((connection) => {
    const targetType = nodesRef.current.find((node) => node.id === connection.target)?.type || '';
    const selectedSources = nodesRef.current.filter((node) => node.selected && isConnectionSource(node, targetType));
    const sourceIds = selectedSources.some((node) => node.id === connection.source)
      ? selectedSources.map((node) => node.id)
      : [connection.source];
    sourceIds.forEach((sourceId) => addReference(sourceId, connection.target));
  }, [addReference]);

  const handleValidConnection = useCallback((connection) => {
    const targetType = nodesRef.current.find((node) => node.id === connection.target)?.type || '';
    return Boolean(sourceConnectionOption(nodesRef.current.find((node) => node.id === connection.source), targetType))
      && ['imageGenerator', 'videoGenerator'].includes(targetType)
      && !edgesRef.current.some((edge) => edge.source === connection.source && edge.target === connection.target);
  }, []);

  const onNodeDrag = useCallback((_event, dragged) => {
    dragLatestRef.current = dragged;
    if (guideFrameRef.current) return;
    guideFrameRef.current = window.requestAnimationFrame(() => {
      guideFrameRef.current = 0;
      const currentDrag = dragLatestRef.current;
      if (!currentDrag) return;
      const zoom = viewportRef.current.zoom || 1;
      const threshold = 7 / zoom;
      const draggedSize = nodeSize(currentDrag);
      const draggedX = [currentDrag.position.x, currentDrag.position.x + draggedSize.width / 2, currentDrag.position.x + draggedSize.width];
      const draggedY = [currentDrag.position.y, currentDrag.position.y + draggedSize.height / 2, currentDrag.position.y + draggedSize.height];
      let bestX = null; let bestY = null;
      nodesRef.current.forEach((node) => {
        if (node.id === currentDrag.id) return;
        const size = nodeSize(node);
        const pointsX = [node.position.x, node.position.x + size.width / 2, node.position.x + size.width];
        const pointsY = [node.position.y, node.position.y + size.height / 2, node.position.y + size.height];
        draggedX.forEach((value, ownIndex) => pointsX.forEach((target) => {
          const diff = target - value;
          if (Math.abs(diff) <= threshold && (!bestX || Math.abs(diff) < Math.abs(bestX.diff))) bestX = { diff, guide: target, ownIndex };
        }));
        draggedY.forEach((value, ownIndex) => pointsY.forEach((target) => {
          const diff = target - value;
          if (Math.abs(diff) <= threshold && (!bestY || Math.abs(diff) < Math.abs(bestY.diff))) bestY = { diff, guide: target, ownIndex };
        }));
      });
      dragSnapRef.current = bestX || bestY ? {
        nodeId: currentDrag.id,
        position: { x: currentDrag.position.x + (bestX?.diff || 0), y: currentDrag.position.y + (bestY?.diff || 0) },
      } : null;
      const pending = { x: bestX?.guide ?? null, y: bestY?.guide ?? null };
      guidePendingRef.current = pending;
      setGuides((current) => current.x === pending.x && current.y === pending.y ? current : pending);
    });
  }, []);

  const finishLooseConnection = useCallback((event, connectionState) => {
    const start = connectionStartRef.current;
    const sourceId = start?.nodeId || connectionState?.fromNode?.id;
    const startsFromOutput = start?.handleType === 'source' || connectionState?.fromHandle?.type === 'source';
    connectionStartRef.current = null;
    if (!sourceId || connectionState?.toNode?.id || !startsFromOutput) return;
    const clientX = Number(event?.clientX);
    const clientY = Number(event?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
    const target = nodesRef.current.find((node) => {
      if (node.id === sourceId || !['imageGenerator', 'videoGenerator'].includes(node.type)) return false;
      const element = wrapperRef.current?.querySelector(`[data-id="${node.id}"]`);
      const rect = element?.getBoundingClientRect();
      return rect && clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    });
    if (!target) {
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      if (!wrapperRect) return;
      setLooseConnection({
        sourceId,
        position: screenToFlowPosition({ x: clientX, y: clientY }),
        x: Math.max(12, Math.min(wrapperRect.width - 232, clientX - wrapperRect.left + 14)),
        y: Math.max(12, Math.min(wrapperRect.height - 138, clientY - wrapperRect.top - 54)),
      });
      return;
    }
    const targetType = target.type;
    const sourceNodes = nodesRef.current.filter((node) => node.selected && isConnectionSource(node, targetType));
    const sourceIds = sourceNodes.some((node) => node.id === sourceId)
      ? sourceNodes.map((node) => node.id)
      : [sourceId];
    sourceIds.forEach((id) => addReference(id, target.id));
  }, [addReference, screenToFlowPosition]);

  useEffect(() => () => {
    window.cancelAnimationFrame(guideFrameRef.current);
    window.cancelAnimationFrame(nodeChangeFrameRef.current);
    pendingNodeChangesRef.current.clear();
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && (target.matches('input,textarea,select') || target.isContentEditable)) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') { event.preventDefault(); event.shiftKey ? redo() : undo(); return; }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') { event.preventDefault(); redo(); return; }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); void saveNow(); return; }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        // React Flow's built-in delete handler is disabled so that custom
        // edge/node deletion stays in sync with our selection affordances.
        // Claim the key here and clear every selection layer in the same turn
        // to avoid the second-click behaviour caused by a stale overlay.
        event.preventDefault();
        event.stopPropagation();
        const selectedNodeIds = nodesRef.current.filter((node) => node.selected).map((node) => node.id);
        const selectedNodeSet = new Set(selectedNodeIds);
        const selectedEdgeIds = [...new Set([
          ...edgesRef.current.filter((edge) => edge.selected && !selectedNodeSet.has(edge.source) && !selectedNodeSet.has(edge.target)).map((edge) => edge.id),
          ...(selectionAction?.edgeIds || []),
        ])];
        if (selectedNodeIds.length) deleteNodes(selectedNodeIds);
        if (selectedEdgeIds.length) deleteSelectedEdges(selectedEdgeIds);
        setSelectionAction(null);
        selectionCompletedRef.current = false;
        selectionPointerDownRef.current = false;
        selectionGestureRef.current = null;
        window.requestAnimationFrame(clearSelectionOverlay);
      }
      if (event.key === 'Escape') {
        setContextMenu(null);
        setLooseConnection(null);
        setUploadOpen(false);
        setSettingsOpen(false);
        setSettingsHover(false);
        setLibraryOpen(false);
        setTaskMonitorOpen(false);
        setMaterialDraft(null);
        setMediaPreview(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [clearSelectionOverlay, deleteNodes, deleteSelectedEdges, redo, saveNow, selectionAction, undo]);

  useEffect(() => {
    const pasteAssets = (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && (target.closest('input, textarea, select, [contenteditable="true"]') || target.isContentEditable)) return;
      const files = [...(event.clipboardData?.files || [])];
      const images = files.filter((file) => fileKind(file) === 'image');
      const videos = files.filter((file) => fileKind(file) === 'video');
      const audios = files.filter((file) => fileKind(file) === 'audio');
      if (!images.length && !videos.length && !audios.length) return;
      event.preventDefault();
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const position = screenToFlowPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      if (images.length) void uploadFiles(images, 'image', position);
      if (videos.length) void uploadFiles(videos, 'video', { x: position.x + 54, y: position.y + 54 });
      if (audios.length) void uploadFiles(audios, 'audio', { x: position.x + 108, y: position.y + 108 });
    };
    window.addEventListener('paste', pasteAssets);
    return () => window.removeEventListener('paste', pasteAssets);
  }, [screenToFlowPosition, uploadFiles]);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const closeOutside = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('.canvas-context-menu')) setContextMenu(null);
    };
    document.addEventListener('pointerdown', closeOutside, true);
    return () => document.removeEventListener('pointerdown', closeOutside, true);
  }, [contextMenu]);

  const guideX = guides.x === null ? null : guides.x * viewportRef.current.zoom + viewportRef.current.x;
  const guideY = guides.y === null ? null : guides.y * viewportRef.current.zoom + viewportRef.current.y;

  return <div
    className={`canvas-workspace theme-${theme} ${arranging ? 'is-arranging' : ''}`}
    ref={wrapperRef}
    onPointerDownCapture={(event) => {
      const target = event.target instanceof Element ? event.target : null;
      // A secondary-button gesture is always a new viewport/context-menu
      // interaction. Do not let it inherit a completed lasso from any node,
      // edge, or overlay underneath the pointer.
      if (event.button === 2 || event.button === 1) {
        clearActiveSelection();
        setContextMenu(null);
        return;
      }
      if (!target?.closest('.react-flow__pane')) {
        // Clicking an external toolbar must also dismiss the old custom
        // selection affordance, otherwise its hit layer can swallow the first
        // click on Upload/Delete.
        if (event.button === 0 && target?.closest('.canvas-toolbar, .canvas-settings-rail, .canvas-settings-dock, .canvas-topbar, .canvas-zoom-controls')) {
          clearActiveSelection();
        }
        return;
      }
      if (event.button === 0) {
        // Finish the previous overlay cleanup before arming the new gesture.
        // The old order could mark the freshly-created React Flow rectangle
        // as stale in the same pointerdown turn, making a held lasso vanish.
        selectionPointerDownRef.current = false;
        selectionGestureRef.current = null;
        clearSelectionOverlay();
        wrapperRef.current?.querySelectorAll('.react-flow__selection, .react-flow__nodesselection-rect')
          .forEach((selection) => selection.classList.remove('canvas-selection-stale'));
        selectionPointerDownRef.current = true;
        selectionCompletedRef.current = false;
        setSelectionAction(null);
        setLooseConnection(null);
      }
    }}
    onPointerCancelCapture={(event) => {
      if (event.button === 0 && selectionPointerDownRef.current) {
        selectionPointerDownRef.current = false;
        selectionGestureRef.current = null;
        selectionCompletedRef.current = false;
        clearSelectionOverlay();
        return;
      }
      if (!(event.target instanceof Element) || !event.target.closest('.react-flow__pane')) return;
      if (event.button === 0) {
        selectionPointerDownRef.current = false;
        selectionGestureRef.current = null;
        selectionCompletedRef.current = false;
        clearSelectionOverlay();
      }
      if (event.button === 1 || event.button === 2) clearActiveSelection();
    }}
    onPointerUpCapture={(event) => {
      // A viewport gesture can finish before React Flow emits onMoveEnd.
      // Clear the lasso hand-off at the native boundary so no stale
      // selection rectangle can be painted into the next pan/zoom frame.
      if (event.button === 0 && selectionPointerDownRef.current) {
        selectionPointerDownRef.current = false;
        selectionGestureRef.current = null;
        window.requestAnimationFrame(clearSelectionOverlay);
        return;
      }
      if (!(event.target instanceof Element) || !event.target.closest('.react-flow__pane')) return;
      if (event.button === 1 || event.button === 2) clearActiveSelection();
    }}
    onContextMenuCapture={(event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('.react-flow')) return;
      // Never expose the browser's native copy/search menu over the canvas.
      // React Flow's pane/node handlers still receive the event and provide
      // the purpose-built cream context menu below.
      event.preventDefault();
      clearActiveSelection();
    }}
    onDragStartCapture={(event) => {
      // Canvas media is moved with React Flow pointer gestures. Prevent the
      // browser's HTML drag ghost from competing with lasso/pan interactions.
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.react-flow__pane') && target.closest('img,video')) event.preventDefault();
    }}
  >
    <CanvasTopbar
      project={project} projects={projects} saveState={saveState}
      canUndo={historyRef.current.undo.length > 0 || historyVersion < 0}
      canRedo={historyRef.current.redo.length > 0}
      user={user} theme={theme} onTheme={onTheme} onProject={onProject} onCreateProject={onCreateProject}
      onUndo={undo} onRedo={redo} onFit={() => fitView({ padding: 0.16, duration: 450, maxZoom: 1.05 })}
      accountActions={accountActions} onRenameProject={onRenameProject} onDeleteProject={onDeleteProject}
    />
    <input ref={fileInputRef} type="file" hidden onChange={(event) => {
      const pending = pendingPickerRef.current;
      void uploadFiles(event.target.files, pending.kind, pending.position, pending.nodeId);
      event.target.value = '';
    }} />
    <ReactFlow
      nodes={displayNodes}
      edges={displayEdges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onlyRenderVisibleElements
      onNodesChange={handleNodesChange}
      onEdgesChange={handleEdgesChange}
      onConnect={handleConnect}
      onConnectStart={handleConnectStart}
      onConnectEnd={finishLooseConnection}
      onEdgeClick={(event, edge) => {
        event.stopPropagation();
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect) return;
        const additive = Boolean(event.shiftKey || event.ctrlKey || event.metaKey);
        const edgeIds = additive
          ? [...new Set([...(selectionAction?.edgeIds || []), edge.id])]
          : [edge.id];
        setSelectionAction({
          x: Math.max(8, Math.min(rect.width - 48, event.clientX - rect.left - 20)),
          y: Math.max(8, Math.min(rect.height - 48, event.clientY - rect.top - 20)),
          width: 40,
          height: 40,
          count: 0,
          edgeIds,
        });
      }}
      connectionLineComponent={CanvasConnectionLine}
      connectionLineStyle={{ stroke: '#2e70e8', strokeWidth: 3, strokeLinecap: 'round' }}
      isValidConnection={handleValidConnection}
      onNodeDragStart={() => {
        dragActiveRef.current = true;
        beginMovement('node');
        dragSnapshotRef.current = snapshot();
        dragSnapRef.current = null;
      }}
      onNodeDrag={onNodeDrag}
      onNodeDragStop={() => {
        flushPendingNodeChanges();
        if (guideFrameRef.current) window.cancelAnimationFrame(guideFrameRef.current);
        guideFrameRef.current = 0;
        dragLatestRef.current = null;
        if (dragSnapRef.current) {
          const snap = dragSnapRef.current;
          const dragSelection = new Set(
            (dragSnapshotRef.current?.nodes || [])
              .filter((node) => node.selected)
              .map((node) => node.id),
          );
          dragSelection.add(snap.nodeId);
          updateNodesState((current) => {
            const draggedNode = current.find((node) => node.id === snap.nodeId);
            if (!draggedNode) return current;
            const delta = {
              x: snap.position.x - draggedNode.position.x,
              y: snap.position.y - draggedNode.position.y,
            };
            return current.map((node) => dragSelection.has(node.id)
              ? { ...node, position: { x: node.position.x + delta.x, y: node.position.y + delta.y } }
              : node);
          });
        }
        if (dragSnapshotRef.current) checkpoint(dragSnapshotRef.current);
        dragSnapshotRef.current = null;
        dragSnapRef.current = null;
        dragActiveRef.current = false;
        endMovement('node');
        markDirty();
        setGuides((current) => current.x === null && current.y === null ? current : { x: null, y: null });
      }}
      onMoveStart={(event) => {
        // With selectionOnDrag enabled React Flow can notify the viewport
        // pan layer before the selection threshold is crossed. Do not let
        // that intermediate callback clear/hide the live selection box.
        if (selectionPointerDownRef.current || selectionGestureRef.current) return;
        // A viewport pan must leave box-selection mode immediately. Otherwise
        // ReactFlow keeps the old selection overlay alive and paints a ghost
        // rectangle over the next pan gesture.
        if (event?.button === 1 || event?.button === 2) clearActiveSelection();
        else {
          setSelectionAction(null);
          setLooseConnection(null);
          selectionGestureRef.current = null;
          clearSelectionOverlay();
        }
        // Only pointer panning needs the moving visual state.  Wheel/pinch
        // zooms also emit onMoveStart, but toggling that class there causes
        // every node shadow and the minimap to flash on each zoom tick.
        if (event?.button === 1 || event?.button === 2 || event?.type === 'pointerdown') beginMovement('viewport');
        setGuides((current) => current.x === null && current.y === null ? current : { x: null, y: null });
      }}
      onMove={handleMove}
      onMoveEnd={(_event, nextViewport) => {
        if (selectionPointerDownRef.current || selectionGestureRef.current) return;
        viewportRef.current = nextViewport;
        selectionGestureRef.current = null;
        clearSelectionOverlay();
        endMovement('viewport');
        setViewportVersion((value) => value + 1);
        markDirty();
      }}
      onSelectionStart={(event) => {
        selectionCompletedRef.current = false;
        selectionGestureRef.current = Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)
          ? { x: event.clientX, y: event.clientY }
          : null;
        setSelectionAction(null);
        setLooseConnection(null);
        setContextMenu(null);
      }}
      onSelectionEnd={(event) => {
        const wrapper = wrapperRef.current;
        const selection = wrapper?.querySelector('.react-flow__selection');
        const start = selectionGestureRef.current;
        selectionGestureRef.current = null;
        // React Flow can end its internal lasso before the browser emits the
        // native pointerup. Keep the native gesture alive until the capture
        // handler above receives that pointerup, otherwise the cleanup below
        // can hide a still-visible rectangle while the user is holding down.
        const endX = Number(event?.clientX);
        const endY = Number(event?.clientY);
        const gestureRect = start && Number.isFinite(endX) && Number.isFinite(endY) ? {
          left: Math.min(start.x, endX),
          right: Math.max(start.x, endX),
          top: Math.min(start.y, endY),
          bottom: Math.max(start.y, endY),
          width: Math.abs(endX - start.x),
          height: Math.abs(endY - start.y),
        } : null;
        const selectionRect = gestureRect || selection?.getBoundingClientRect();
        const wrapperRect = wrapper?.getBoundingClientRect();
        const hasCompletedLasso = Boolean(selectionRect && wrapperRect && selectionRect.width > 3 && selectionRect.height > 3);
        selectionCompletedRef.current = hasCompletedLasso;
        if (hasCompletedLasso) {
          const selectedSources = nodesRef.current.filter((node) => node.selected && isConnectionSource(node, 'videoGenerator'));
          const edgeIds = selectedSources.length >= 2 ? [] : [...(wrapper.querySelectorAll('[data-edge-id]') || [])]
            .filter((element) => {
              const path = element.querySelector('.canvas-edge__hit');
              if (!path?.getTotalLength || !path?.getPointAtLength) return false;
              const length = path.getTotalLength();
              const matrix = path.getScreenCTM?.();
              if (!matrix || !length) return false;
              const samples = Math.max(18, Math.min(72, Math.ceil(length / 24)));
              for (let index = 0; index <= samples; index += 1) {
                const point = path.getPointAtLength(length * index / samples);
                const screenPoint = new DOMPoint(point.x, point.y).matrixTransform(matrix);
                if (screenPoint.x >= selectionRect.left && screenPoint.x <= selectionRect.right
                  && screenPoint.y >= selectionRect.top && screenPoint.y <= selectionRect.bottom) return true;
              }
              return false;
            })
            .map((element) => element.getAttribute('data-edge-id'))
            .filter((id) => id && edgesRef.current.some((edge) => edge.id === id));
          if (edgeIds.length && selectedSources.length < 2) {
            updateNodesState((current) => current.map((node) => node.selected ? { ...node, selected: false } : node));
            setSelectionAction({
              x: selectionRect.left - wrapperRect.left,
              y: selectionRect.top - wrapperRect.top,
              width: selectionRect.width,
              height: selectionRect.height,
              count: 0,
              edgeIds: [...new Set(edgeIds)],
            });
          }
        }
        setViewportVersion((value) => value + 1);
      }}
      onPaneClick={() => {
        const preserveCompletedSelection = selectionCompletedRef.current;
        selectionCompletedRef.current = false;
        selectionPointerDownRef.current = false;
        selectionGestureRef.current = null;
        clearSelectionOverlay();
        setContextMenu(null);
        setLooseConnection(null);
        // A lasso is followed by a synthetic pane click in React Flow. Keep
        // the computed box/action for that click; a real blank click still
        // clears it normally.
        if (!preserveCompletedSelection) setSelectionAction(null);
        setUploadOpen(false);
      }}
      onPaneContextMenu={(event) => {
        event.preventDefault();
        clearActiveSelection();
        const rect = wrapperRef.current.getBoundingClientRect();
        setContextMenu({ kind: 'pane', x: event.clientX - rect.left, y: event.clientY - rect.top, position: screenToFlowPosition({ x: event.clientX, y: event.clientY }) });
      }}
      onNodeContextMenu={(event, node) => {
        event.preventDefault();
        const rect = wrapperRef.current.getBoundingClientRect();
        setContextMenu({ kind: 'node', nodeId: node.id, x: event.clientX - rect.left, y: event.clientY - rect.top });
      }}
      onDrop={(event) => {
        event.preventDefault();
        const libraryPayload = event.dataTransfer.getData('application/x-nebula-library');
        if (libraryPayload) {
          try {
            const asset = JSON.parse(libraryPayload);
            addLibraryAsset(asset, asset.name || asset.fallbackName, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
          } catch { notify('历史素材读取失败', 'error'); }
          return;
        }
        const favoritePayload = event.dataTransfer.getData('application/x-nebula-favorite');
        if (favoritePayload) {
          try {
            addSettingMaterial(JSON.parse(favoritePayload), screenToFlowPosition({ x: event.clientX, y: event.clientY }));
          } catch {
            notify('收藏素材数据无效', 'error');
          }
          return;
        }
        const framePayload = event.dataTransfer.getData('application/x-nebula-video-frame') || event.dataTransfer.getData('application/tap-frame-sample');
        if (framePayload) {
          try {
            const frame = JSON.parse(framePayload);
            addVideoFrame(frame, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
          } catch {
            notify('视频帧格式无效', 'error');
          }
          return;
        }
        const files = [...(event.dataTransfer.files || [])];
        const externalUrl = files.length ? '' : droppedUrl(event.dataTransfer);
        if (!files.length && externalUrl) {
          void fileFromDroppedUrl(externalUrl).then((file) => {
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            const kind = fileKind(file);
            if (!kind) throw new Error('无法识别外部素材类型');
            return uploadFiles([file], kind, position);
          }).catch((error) => notify(error.message || '外部素材读取失败', 'error'));
          return;
        }
        const images = files.filter((file) => fileKind(file) === 'image');
        const videos = files.filter((file) => fileKind(file) === 'video');
        const audios = files.filter((file) => fileKind(file) === 'audio');
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        if (images.length) void uploadFiles(images, 'image', position);
        if (videos.length) void uploadFiles(videos, 'video', { x: position.x + 48, y: position.y + 48 });
        if (audios.length) void uploadFiles(audios, 'audio', { x: position.x + 96, y: position.y + 96 });
      }}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
      panOnDrag={[1, 2]}
      selectionOnDrag
      selectionMode={SelectionMode.Partial}
      zoomOnScroll
      zoomOnPinch
      minZoom={0.08}
      maxZoom={4}
      deleteKeyCode={null}
      multiSelectionKeyCode="Shift"
      elevateNodesOnSelect
      proOptions={{ hideAttribution: true }}
    >
      <Background variant={BackgroundVariant.Dots} gap={16} size={1.35} color="var(--canvas-grid)" />
      {showMap && <CanvasMiniMap
        nodes={nodes}
        viewport={viewportRef.current}
        viewportVersion={viewportVersion}
        wrapperRef={wrapperRef}
        theme={theme}
        setCenter={setCenter}
        pulseNodeId={minimapPulseNodeId}
      />}
    </ReactFlow>
    {looseConnection && (looseConnectionCapabilities.image || looseConnectionCapabilities.video) && <div
      className="canvas-loose-connect-menu nodrag nopan"
      style={{ left: looseConnection.x, top: looseConnection.y }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <small>添加节点</small>
      {looseConnectionCapabilities.image && <button type="button" onClick={() => createConnectedAt('image', looseConnection.sourceId, looseConnection.position)}><ImagePlus size={18} /><b>图片</b></button>}
      {looseConnectionCapabilities.video && <button type="button" onClick={() => createConnectedAt('video', looseConnection.sourceId, looseConnection.position)}><Video size={18} /><b>视频</b></button>}
    </div>}
    {batchConnectionDrag && <svg className="canvas-batch-connection-preview" aria-hidden="true">
      <path d={`M ${batchConnectionDrag.start.x} ${batchConnectionDrag.start.y} C ${(batchConnectionDrag.start.x + batchConnectionDrag.end.x) / 2} ${batchConnectionDrag.start.y}, ${(batchConnectionDrag.start.x + batchConnectionDrag.end.x) / 2} ${batchConnectionDrag.end.y}, ${batchConnectionDrag.end.x} ${batchConnectionDrag.end.y}`} />
    </svg>}
    {selectionAction?.edgeIds?.length && <div
      className="canvas-edge-selection-box nodrag nopan"
      style={{ left: selectionAction.x, top: selectionAction.y, width: selectionAction.width, height: selectionAction.height }}
      onPointerDown={(event) => event.stopPropagation()}
    ><button type="button" title={`断开 ${selectionAction.edgeIds.length} 条连线`} onClick={() => deleteSelectedEdges(selectionAction.edgeIds)}><X size={18} /></button></div>}
    {selectionAction && !selectionAction.edgeIds?.length && selectionAction.box && <div
      className="canvas-multi-selection-box nodrag nopan"
      style={{ left: selectionAction.box.x, top: selectionAction.box.y, width: selectionAction.box.width, height: selectionAction.box.height }}
      aria-hidden="true"
    />}
    {selectionAction && !selectionAction.edgeIds?.length && selectionAction.count >= 2 && <div className="canvas-selection-action nodrag nopan" style={{ left: selectionAction.x, top: selectionAction.y }} onPointerDown={(event) => event.stopPropagation()}>
      <span className="canvas-selection-action__label">连接 {selectionAction.count} 个素材</span>
      <button type="button" className="canvas-selection-action__connector" title="拖动连接选中素材" onPointerDown={startBatchConnection}><span className="selection-connector-ball" /></button>
    </div>}
    {!ready && <div className="canvas-loading"><LoaderCircle className="spin" size={24} /><span>正在打开项目画布</span></div>}
    <aside className="canvas-toolbar" aria-label="画布工具">
      <div
        className="toolbar-upload"
      >
      <button type="button" title="选择上传类型" onClick={() => setUploadOpen((value) => !value)} aria-expanded={uploadOpen}><Upload size={19} /><span className="canvas-tool-label">上传</span></button>
        {uploadOpen && <div className="canvas-popover upload-menu">
          <button type="button" onClick={() => openPicker('image')}><FileImage size={16} />上传图片</button>
          <button type="button" onClick={() => openPicker('video')}><FileVideo size={16} />上传视频</button>
          <button type="button" onClick={() => openPicker('audio')}><FileAudio size={16} />上传音频</button>
        </div>}
      </div>
      <span />
      <div className="canvas-create-group" aria-label="创建容器">
        <button type="button" title="创建生图容器" onClick={() => addGenerator('image')}><ImagePlus size={19} /><span className="canvas-tool-label">创建生图</span></button>
        <button type="button" title="创建生视频容器" onClick={() => addGenerator('video')}><Video size={19} /><span className="canvas-tool-label">创建视频</span></button>
        <button type="button" title="创建音频容器" onClick={() => addEmptyAudio()}><FileAudio size={19} /><span className="canvas-tool-label">创建音频</span></button>
      </div>
      <span />
      <button type="button" className={taskMonitorOpen ? 'is-active' : ''} title="生成任务监控" onClick={() => setTaskMonitorOpen((value) => !value)}><ListChecks size={19} /><span className="canvas-tool-label">任务</span></button>
      <button type="button" title="一键整理画布" onClick={arrange}><AlignHorizontalDistributeCenter size={19} /><span className="canvas-tool-label">整理</span></button>
    </aside>
    <div
      className={`canvas-favorites-zone ${settingsOpen ? 'is-open' : ''}`}
    >
      <button type="button" className="canvas-settings-rail" title="打开收藏夹" onClick={() => setSettingsOpen((value) => !value)}><Images size={18} /><span>收藏夹</span></button>
      {settingsOpen && <div className="canvas-settings-dock">
        <CanvasSettingsPanel
          materials={materials}
          project={project}
          onAdd={addSettingMaterial}
          onClose={() => { setSettingsOpen(false); setSettingsHover(false); }}
          onOpenLibrary={() => setLibraryOpen(true)}
          onPreview={setMediaPreview}
          onRefresh={onSessionRefresh}
          onNotify={notify}
          onUploadStateChange={setFavoritesUploadLock}
        />
      </div>}
    </div>
    <CanvasTaskMonitor open={taskMonitorOpen} tasks={tasks} videoTasks={videoTasks} onClose={() => setTaskMonitorOpen(false)} onRefresh={onSessionRefresh} onNotify={notify} onRetry={onRetryTask} />
    <div className="canvas-zoom-controls">
      <button type="button" onClick={() => zoomOut({ duration: 180 })} title="缩小"><Minus size={16} /></button>
      <b>{Math.round((viewportRef.current.zoom || 1) * 100)}%</b>
      <button type="button" onClick={() => zoomIn({ duration: 180 })} title="放大"><ZoomIn size={16} /></button>
      <button type="button" onClick={() => fitView({ padding: 0.16, duration: 450, maxZoom: 1.05 })} title="适应内容"><Maximize2 size={16} /></button>
      <button type="button" className={showMap ? 'is-active' : ''} onClick={() => setShowMap((value) => !value)} title="小地图"><MapIcon size={16} /></button>
    </div>
    {guideX !== null && <i className="canvas-guide is-vertical" style={{ left: guideX }} />}
    {guideY !== null && <i className="canvas-guide is-horizontal" style={{ top: guideY }} />}
    {contextMenu && <div className="canvas-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
      <button type="button" className="context-close" onClick={() => setContextMenu(null)}><X size={14} /></button>
      {contextMenu.kind === 'pane' ? <>
        <button type="button" onClick={() => openPicker('image', contextMenu.position)}><FileImage size={15} />上传图片</button>
        <button type="button" onClick={() => openPicker('video', contextMenu.position)}><FileVideo size={15} />上传视频</button>
        <button type="button" onClick={() => openPicker('audio', contextMenu.position)}><FileAudio size={15} />上传音频</button>
        <em />
        <button type="button" onClick={() => addGenerator('image', contextMenu.position)}><ImagePlus size={15} />创建生图</button>
        <button type="button" onClick={() => addGenerator('video', contextMenu.position)}><Video size={15} />创建生视频</button>
        <button type="button" onClick={() => addEmptyAudio(contextMenu.position)}><FileAudio size={15} />创建音频容器</button>
        <em />
        <button type="button" onClick={arrange}><AlignHorizontalDistributeCenter size={15} />整理画布</button>
      </> : <>
        <button type="button" onClick={() => duplicateNode(contextMenu.nodeId)}><Plus size={15} />复制节点</button>
        <button type="button" onClick={() => { checkpoint(); updateEdgesState((current) => current.filter((edge) => edge.source !== contextMenu.nodeId && edge.target !== contextMenu.nodeId)); markDirty(); setContextMenu(null); }}><X size={15} />断开连接</button>
        <em />
        <button type="button" className="is-danger" onClick={() => deleteNodes([contextMenu.nodeId])}><X size={15} />从画布移除</button>
      </>}
    </div>}
    {mediaPreview && <CanvasMediaPreview
      media={mediaPreview}
      onClose={() => setMediaPreview(null)}
      onDownload={() => downloadMedia(mediaPreview)}
    />}
    {libraryOpen && <CanvasAssetLibrary
      tasks={tasks}
      videoTasks={videoTasks}
      onAdd={addLibraryAsset}
      onClose={() => setLibraryOpen(false)}
    />}
    <CanvasMaterialSaveDialog
      source={materialDraft}
      collections={project.materialCollections || []}
      initialCollectionId={project.materialCollections?.[0]?.id || ''}
      busy={materialBusy}
      onClose={() => setMaterialDraft(null)}
      onSubmit={saveCanvasImageAsMaterial}
    />
  </div>;
}

function pathName(filename) {
  return String(filename || '素材').replace(/\.[^.]+$/u, '').slice(0, 100) || '素材';
}

export default function CanvasWorkspace(props) {
  return <ReactFlowProvider><CanvasWorkspaceInner {...props} /></ReactFlowProvider>;
}
